/**
 * Staff — admin-only home for Defera's AI staff.
 *
 * Architecture (built to scale as the AI team grows):
 *   - The roster is registry-driven: GET /api/admin/staff returns every
 *     member with generic `headline` stats ([{label, value}]), so a new
 *     teammate renders a live roster card with ZERO page changes.
 *   - Each member owns a "desk" (their workspace) resolved from
 *     DESK_REGISTRY below. Members without a dedicated desk fall back to
 *     GenericDesk — again, nothing breaks when the backend grows first.
 *   - Selection is URL-mirrored (?member=stephanie) so desks are
 *     deep-linkable, matching the Activity page's pattern.
 *
 * Current desks:
 *   - Stephanie (AI Concierge Quality Manager): her review desk — verdict
 *     stats, filterable/paginated review feed over Tommy's replies, and
 *     the on-demand audit runner.
 *   - Tommy (AI Concierge): performance snapshot + jump-offs to the
 *     concierge inbox and to Stephanie's oversight.
 *
 * Mobile: the roster is an edge-bleed snap carousel below `sm`, a grid
 * above it; every control keeps ≥40px tap targets; the workspace scrolls
 * into view on selection.
 *
 * Security posture: all data routes are requireAdmin()-gated server-side.
 * The client-side role check only picks which UI to render.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { computeTreeLayout, computeStoryLayout, edgePath } from '@/lib/orgTree';
import {
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Sparkles,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  GraduationCap,
  MessageSquare,
  ArrowUpRight,
  Plane,
  Cpu,
  Radio,
  CornerDownRight,
  Users,
  ClipboardCheck,
  Gauge,
  Clock3,
  Crown,
  Share2,
  LayoutGrid,
  Inbox,
  Maximize2,
  X,
  MessagesSquare,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { User as UserEntity } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/utils';
import VipBadge from '@/components/admin/VipBadge';

// ---------------------------------------------------------------------------
// Access + shared helpers
// ---------------------------------------------------------------------------

// Admin role check — mirrors Layout.jsx / Activity.jsx. Backed by JWT
// app_metadata.roles (users cannot self-edit those, unlike user_metadata).
function isCurrentUserAdmin(u) {
  if (!u) return false;
  const roles = u?.app_metadata?.roles || u?.appMetadata?.roles || [];
  return (Array.isArray(roles) && roles.includes('admin')) || u?.role === 'admin';
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const diff = Date.now() - then;
    if (diff < 60_000) return 'just now';
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// Static fallbacks so the page still paints if /api/admin/staff is
// briefly unreachable. Kept in sync with server/ai-staff.js.
const FALLBACK_DEPARTMENTS = [
  { id: 'concierge', name: 'Concierge', blurb: 'Customer-facing service and its quality oversight.' },
  { id: 'risk-intelligence', name: 'Risk & Intelligence', blurb: 'Portfolio risk, liquidity, and decision analytics.' },
  { id: 'finance-accounting', name: 'Finance & Accounting', blurb: 'The company books — revenue quality, cash versus accrual, customer liabilities, and close discipline.' },
  { id: 'customer-insights', name: 'Customer Insights', blurb: 'How customers actually move through the product — funnels, friction, and behavior trends.' },
  { id: 'lifecycle-marketing', name: 'Lifecycle Marketing', blurb: 'Every automated customer email — nurture, receipts, notices — and whether anyone reads them.' },
  { id: 'loyalty-rewards', name: 'Loyalty & Rewards', blurb: 'Defera Miles, status benefits, and the perks that keep customers coming back.' },
  { id: 'payment-operations', name: 'Payment Operations', blurb: 'Installment billing, dunning, and recovery.' },
  { id: 'booking-operations', name: 'Booking Operations', blurb: 'Fulfillment after checkout — airline confirmations, seats, changes, and trips that depart clean.' },
  { id: 'security-reliability', name: 'Security & Reliability', blurb: 'Application security, uptime, and configuration integrity.' },
];

const FALLBACK_STAFF = [
  {
    id: 'stephanie',
    name: 'Stephanie',
    title: 'AI Concierge Quality Manager',
    department: 'concierge',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/stephanie.png',
    surface: 'Internal — Staff page reviews',
    model: 'gpt-4o',
    summary:
      "Tommy's manager. Audits each of Tommy's replies for factual accuracy against the full Defera knowledge base and for tone drift, and files corrections that Tommy delivers to the customer on his next turn.",
    headline: [],
  },
  {
    id: 'tommy',
    name: 'Tommy',
    title: 'AI Concierge Specialist',
    department: 'concierge',
    rank: 'specialist',
    reportsTo: 'stephanie',
    avatar: '/staff/tommy.png',
    surface: 'Customer chat — /support',
    model: 'gpt-4o',
    summary:
      'Front-line concierge. Answers every customer question about Defera — products, payments, trips, seats, cancellations — grounded strictly in the Defera knowledge base, with live account and booking context when the customer is signed in.',
    headline: [],
  },
  {
    id: 'camille',
    name: 'Camille',
    title: 'AI Concierge Specialist',
    department: 'concierge',
    rank: 'specialist',
    reportsTo: 'stephanie',
    avatar: '/staff/camille.png',
    surface: 'Customer chat — /support',
    model: 'gpt-4o',
    summary:
      "Concierge specialist on the same desk as Tommy — identical training, knowledge base, and consult flow. The desk rotates between visits, so any specialist may pick up any customer's thread.",
    headline: [],
  },
  {
    id: 'andre',
    name: 'Andre',
    title: 'AI Concierge Specialist',
    department: 'concierge',
    rank: 'specialist',
    reportsTo: 'stephanie',
    avatar: '/staff/andre.png',
    surface: 'Customer chat — /support',
    model: 'gpt-4o',
    summary:
      "Concierge specialist on the same desk as Tommy — identical training, knowledge base, and consult flow. The desk rotates between visits, so any specialist may pick up any customer's thread.",
    headline: [],
  },
  {
    id: 'isabella',
    name: 'Isabella',
    title: 'AI Concierge Specialist',
    department: 'concierge',
    rank: 'specialist',
    reportsTo: 'stephanie',
    avatar: '/staff/isabella.png',
    surface: 'Customer chat — /support',
    model: 'gpt-4o',
    summary:
      "Concierge specialist on the same desk as Tommy — identical training, knowledge base, and consult flow. The desk rotates between visits, so any specialist may pick up any customer's thread.",
    headline: [],
  },
  {
    id: 'kai',
    name: 'Kai',
    title: 'AI Concierge Specialist',
    department: 'concierge',
    rank: 'specialist',
    reportsTo: 'stephanie',
    avatar: '/staff/kai.png',
    surface: 'Customer chat — /support',
    model: 'gpt-4o',
    summary:
      "Concierge specialist on the same desk as Tommy — identical training, knowledge base, and consult flow. The desk rotates between visits, so any specialist may pick up any customer's thread.",
    headline: [],
  },
  {
    id: 'elliott',
    name: 'Elliott',
    title: 'AI Risk Intelligence Director',
    department: 'risk-intelligence',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/elliott.png',
    surface: 'Internal — Staff desk · daily risk report',
    model: 'Deterministic risk engine + gpt-4o narratives',
    summary:
      "Vera's and Marcus's manager. His daily report is the layer above their analysis: verifying their intelligence pipelines are actually producing (a snapshot engine that goes dark is itself a risk event), reading the portfolio's posture and the default ladder's flow in aggregate, and pulling Ezra's behavior digest in as a leading indicator — the whole risk picture on one page.",
    headline: [],
  },
  {
    id: 'vera',
    name: 'Vera',
    title: 'AI Risk Analyst',
    department: 'risk-intelligence',
    rank: 'specialist',
    reportsTo: 'elliott',
    avatar: '/staff/vera.png',
    surface: 'Internal — Risk Review · Risk Insights · Model Performance',
    model: 'Risk engine + gpt-4o narratives',
    summary:
      'Scores every active installment booking for default and fare-spike risk, runs the portfolio risk scans behind Risk Review, and reads Stripe Radar signals in Risk Insights. Model Performance is her scorecard.',
    headline: [],
  },
  {
    id: 'marcus',
    name: 'Marcus',
    title: 'AI Portfolio Analyst',
    department: 'risk-intelligence',
    rank: 'specialist',
    reportsTo: 'elliott',
    avatar: '/staff/marcus.png',
    surface: 'Internal — Portfolio Intelligence',
    model: 'Deterministic snapshot engine',
    summary:
      'Builds the Portfolio Intelligence snapshots: portfolio health score, liquidity coverage, receivables vs. booking liabilities, fare-spike exposure, and stress tests.',
    headline: [],
  },
  {
    id: 'bernard',
    name: 'Bernard',
    title: 'AI Corporate Controller',
    department: 'finance-accounting',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/bernard.png',
    surface: 'Internal — Staff desk · daily books report',
    model: 'Deterministic ledger engine + gpt-4o narratives',
    summary:
      "The team's accountant — an industry-veteran corporate controller and CPA in temperament. His daily books report reads the same activity the Analytics page charts through an accountant's lens: which billing is Defera's own earned revenue versus pass-through airfare, what cash has actually landed versus what's merely billed, and what we owe customers against the cash on hand to cover it.",
    headline: [],
  },
  {
    id: 'iris',
    name: 'Iris',
    title: 'AI Conversion Strategist',
    department: 'customer-insights',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/iris.png',
    surface: 'Internal — Staff desk · daily strategy run',
    model: 'gpt-4o strategy engine + deterministic fallback',
    summary:
      "Ezra's manager. She knows where every piece of data, content, and UI flow lives in the app — and when Ezra's behavior digests show customers stalling, looping, or leaking, she connects the pattern to the exact surface involved and files concrete refinement recommendations to drive conversion and fluidity.",
    headline: [],
  },
  {
    id: 'ezra',
    name: 'Ezra',
    title: 'AI Behavioral Insights Lead',
    department: 'customer-insights',
    rank: 'lead',
    reportsTo: 'iris',
    avatar: '/staff/ezra.png',
    surface: 'Internal — Staff desk · daily behavior digest',
    model: 'Deterministic funnel engine + gpt-4o narratives',
    summary:
      "Studies how customers actually move through Defera — where checkouts stall, who searches again after abandoning, which routes get walked away from, and what people had to ask the concierge because the product didn't make it obvious. Files a daily behavior digest that feeds Iris's strategy run; he observes every surface and commands none of them.",
    headline: [],
  },
  {
    id: 'priya',
    name: 'Priya',
    title: 'AI Search Quality Analyst',
    department: 'customer-insights',
    rank: 'specialist',
    reportsTo: 'iris',
    avatar: '/staff/priya.png',
    surface: 'Internal — Staff desk · search quality telemetry',
    model: 'Deterministic telemetry engine (no LLM)',
    summary:
      'Watches every search customers run: volumes, the routes they ask for, which engine answered, and — her core signal — searches that came back empty. A zero-result search is a customer who wanted to give us money and got a shrug; her job is making sure that list stays short and Iris hears about the routes on it.',
    headline: [],
  },
  {
    id: 'owen',
    name: 'Owen',
    title: 'AI Booking Operations Manager',
    department: 'booking-operations',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/owen.png',
    surface: 'Internal — Staff desk · fulfillment queue',
    model: 'Deterministic fulfillment tracking (no LLM)',
    summary:
      "Owns what happens after the money: every confirmed trip that is still waiting on an airline confirmation number sits in his queue, and seats, flight changes, and departures are his to keep clean. He also keeps the Faye travel insurance queue: each policy ops must buy in Faye's advisor portal once its trip is ticketed, and the premium refunds and free-look cancellations a trip that ends early leaves behind. The promise made at checkout is kept at the gate — his desk is the distance between those two moments.",
    headline: [],
  },
  {
    id: 'margot',
    name: 'Margot',
    title: 'AI Lifecycle Marketing Manager',
    department: 'lifecycle-marketing',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/margot.png',
    surface: 'Internal — Staff desk · daily email report',
    model: 'Deterministic send metrics + gpt-4o narratives',
    summary:
      "Owns every automated customer email — the welcome, the day-7 nurture, payment receipts, seat assignments, default notices, inquiry resolutions. Her daily report tracks what each flow sent and, now that open tracking is live, whether anyone actually reads it. Volume and engagement are hers; delivery failures stay Audrey's alarm.",
    headline: [],
  },
  {
    id: 'roman',
    name: 'Roman',
    title: 'AI Collections & Recovery Specialist',
    department: 'risk-intelligence',
    rank: 'specialist',
    reportsTo: 'elliott',
    avatar: '/staff/roman.png',
    surface: 'Internal — Staff desk · collections & recovery',
    model: 'Deterministic case tracking (no LLM)',
    summary:
      'The face of what happens after a default: collections cases from open to recovered or written off, the passenger blacklist that keeps repeat offenders out, and the road back — reinstatements and credit resolutions for customers who make it right. Firm on the debt, fair on the way back.',
    headline: [],
  },
  {
    id: 'miles',
    name: 'Miles',
    title: 'AI Loyalty Program Manager',
    department: 'loyalty-rewards',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/miles.png',
    surface: 'Internal — Staff desk · Defera Miles & status benefits',
    model: 'Deterministic ledger engine (no LLM)',
    summary:
      'Keeper of Defera Miles and status perks: every earn and redemption in the miles ledger, status bonus awards and benefit claims, and the daily birthday-credit run that surprises customers on their day. His job is simple — make loyalty feel generous while the ledger stays exactly balanced.',
    headline: [],
  },
  {
    id: 'audrey',
    name: 'Audrey',
    title: 'AI Operations Auditor',
    department: 'payment-operations',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/audrey.png',
    surface: 'Internal — Staff desk · hourly ops audit',
    model: 'Deterministic audit engine (no LLM)',
    summary:
      "Penny's manager. Hourly, she verifies the work actually happened: crons left successful heartbeats, webhooks got processed, every automated email has its send on record — and no booking defaults without an independent re-check of the dates.",
    headline: [],
  },
  {
    id: 'penny',
    name: 'Penny',
    title: 'AI Payments Operations Specialist',
    department: 'payment-operations',
    rank: 'specialist',
    reportsTo: 'audrey',
    avatar: '/staff/penny.png',
    surface: 'Internal — Past Due · payment crons',
    model: 'Deterministic crons (no LLM)',
    summary:
      'Keeps installment payments flowing: reminders at 13:00 UTC, the NMI charge run at 14:00, the default-warning escalation ladder at 15:00, plus 15-minute reconcilers — and watches Past Due so nothing slips through.',
    headline: [],
  },
  {
    id: 'silas',
    name: 'Silas',
    title: 'AI Application Security Auditor',
    department: 'security-reliability',
    rank: 'manager',
    reportsTo: null,
    avatar: '/staff/silas.png',
    surface: 'Internal — Staff desk · 4x-daily security sweep',
    model: 'Deterministic audit engine (no LLM)',
    summary:
      'Sweeps the whole app from the outside four times a day — availability and asset integrity, security headers, admin/cron auth gates, anonymous-RLS exposure, env posture, and TLS expiry. Files findings with remediation language and emails the founders the moment a new critical appears. He finds and reports; he never changes anything.',
    headline: [],
  },
  {
    id: 'wren',
    name: 'Wren',
    title: 'AI Reliability Engineer',
    department: 'security-reliability',
    rank: 'specialist',
    reportsTo: 'silas',
    avatar: '/staff/wren.png',
    surface: 'Internal — daily reliability sweep · findings by email',
    model: 'Claude scheduled agent + deterministic sweep scripts',
    summary:
      'Hunts the bugs customers would otherwise find first: a daily sweep of concierge grounding parity, uncollected balances on dead payment plans, stuck reconciliation queues, overdue inquiries, and chat threads where someone had to ask the same question three times. Wren traces each anomaly to the code that caused it and emails the findings — never fixes, never deploys; the report is the product. (Wrens eat bugs.)',
    headline: [],
  },
];

// Rank ordering inside a department: managers lead their section.
const RANK_ORDER = { manager: 0, lead: 1, specialist: 2 };

// Shared roster-row layout: edge-bleed snap carousel on phones, grid from
// `sm` up. One definition so skeleton and department sections can't drift.
const ROSTER_CONTAINER_CLASS =
  'flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-3 px-3 pb-1 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-2 xl:grid-cols-3 sm:overflow-visible';

function prettifyDepartmentId(id) {
  return String(id || 'team')
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Group the roster into ordered department sections. Departments the
 * server knows come first (in server order, with name + blurb); members
 * whose department has no metadata still get a synthesized section, so a
 * backend-first teammate always renders somewhere sensible.
 */
function groupByDepartment(roster, departments) {
  const byDept = new Map();
  for (const m of roster) {
    const deptId = m.department || 'team';
    if (!byDept.has(deptId)) byDept.set(deptId, []);
    byDept.get(deptId).push(m);
  }
  const sortMembers = (members) =>
    [...members].sort((a, b) => (RANK_ORDER[a.rank] ?? 9) - (RANK_ORDER[b.rank] ?? 9));

  const sections = [];
  for (const d of departments || []) {
    if (byDept.has(d.id)) {
      sections.push({ ...d, members: sortMembers(byDept.get(d.id)) });
      byDept.delete(d.id);
    }
  }
  for (const [id, members] of byDept) {
    sections.push({ id, name: prettifyDepartmentId(id), blurb: null, members: sortMembers(members) });
  }
  return sections;
}

// Deterministic per-member avatar gradient — new staff automatically get
// a stable identity color without any page changes.
const AVATAR_GRADIENTS = [
  'from-sky-400/90 to-indigo-600/90',
  'from-fuchsia-400/90 to-violet-600/90',
  'from-emerald-400/90 to-teal-600/90',
  'from-amber-400/90 to-orange-600/90',
  'from-rose-400/90 to-pink-600/90',
];
function gradientFor(id) {
  let h = 0;
  for (const c of String(id || '')) h = ((h * 31) + c.charCodeAt(0)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

// ---------------------------------------------------------------------------
// Org web layout — geometry lives in src/lib/orgTree.js (unit-tested);
// this file only renders what it returns.
//
// Full-hierarchy mode: the human leadership team from the About Us page
// (src/pages/AboutUs.jsx LEADERS — keep names/roles in sync) rendered as an
// executive tier between Defera and the AI departments. Display-only: the
// EXEC_OVERSIGHT mapping below says which executive each AI department
// rolls up to on the chart; it doesn't exist anywhere in the backend.
// ---------------------------------------------------------------------------

/** Mirrors TEAM_IMG_VERSION in AboutUs.jsx — bump both together. */
const LEADERSHIP_IMG_VERSION = 2;

const LEADERSHIP = [
  { id: 'jayjay', name: 'JayJay Gonzalez', title: 'Founder & CEO', department: 'leadership', rank: 'executive', reportsTo: null, avatar: `/images/team/jayjay-gonzalez.jpg?v=${LEADERSHIP_IMG_VERSION}` },
  { id: 'victoria', name: 'Victoria Reynolds', title: 'VP, Global Operations', department: 'leadership', rank: 'executive', reportsTo: 'jayjay', avatar: `/images/team/victoria-reynolds.jpg?v=${LEADERSHIP_IMG_VERSION}` },
  { id: 'luca', name: 'Luca Moretti', title: 'Head of Concierge', department: 'leadership', rank: 'executive', reportsTo: 'jayjay', avatar: `/images/team/luca-moretti.jpg?v=${LEADERSHIP_IMG_VERSION}` },
  { id: 'adrian', name: 'Adrian Calloway', title: 'Head of Marketing', department: 'leadership', rank: 'executive', reportsTo: 'jayjay', avatar: `/images/team/adrian-calloway.jpg?v=${LEADERSHIP_IMG_VERSION}` },
];

/** Which executive each AI department rolls up to in full-hierarchy view. */
const EXEC_OVERSIGHT = {
  concierge: 'luca',
  'risk-intelligence': 'victoria',
  'finance-accounting': 'victoria',
  'payment-operations': 'victoria',
  'security-reliability': 'victoria',
  'customer-insights': 'adrian',
  'lifecycle-marketing': 'adrian',
  'loyalty-rewards': 'adrian',
  'booking-operations': 'victoria',
};

/** Lane order in full-hierarchy view — keeps each executive's departments
 *  contiguous so parents center over their subtrees without strand
 *  crossings (luca | victoria ×5 | adrian ×3). */
const FULL_TREE_DEPT_ORDER = ['concierge', 'risk-intelligence', 'finance-accounting', 'payment-operations', 'security-reliability', 'booking-operations', 'customer-insights', 'lifecycle-marketing', 'loyalty-rewards'];


/** Deterministic fallback fill when a member has no portrait. */
function fallbackFill(id) {
  let h = 0;
  for (const c of String(id || '')) h = ((h * 31) + c.charCodeAt(0)) >>> 0;
  return `hsl(${h % 360}, 45%, 42%)`;
}

/** Diagram title: the roster's "AI " prefix is org-chart noise — strip it,
 *  and wrap long titles to two lines so neighbors never collide. maxChars
 *  tightens for dense story rows where columns are narrower. */
function diagramTitleLines(title, maxChars = 24) {
  const t = String(title || '').replace(/^AI\s+/, '');
  if (t.length <= maxChars) return t ? [t] : [];
  const mid = Math.floor(t.length / 2);
  let split = -1;
  for (let i = 0; i < t.length; i += 1) {
    if (t[i] === ' ' && (split === -1 || Math.abs(i - mid) < Math.abs(split - mid))) split = i;
  }
  return split === -1 ? [t] : [t.slice(0, split), t.slice(split + 1)];
}

function OrgWeb({ sections, roster, selectedId, onSelect, showTitles = false, storyLeadership = [], storyOversight = {}, storyDeptOrder = [] }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  // Fullscreen framing: 'wide' = the landscape tree; 'story' = the
  // Instagram-story (1080×1920) full-hierarchy composition. Phones default
  // to story, desktop to wide — both switchable in the overlay header.
  const [fsMode, setFsMode] = useState(null); // null = auto on open

  // Fullscreen overlay — the whole diagram scaled to fit one screen
  // (SVG viewBox + preserveAspectRatio does the fitting), so it's
  // screenshotable on desktop and phones alike. Escape closes; body
  // scroll locks while open.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded]);

  // Compact canvas on phones (bigger nodes relative to the viewBox so
  // labels stay legible). Width grows with the roster (min slot spacing),
  // so a bigger team widens the tree instead of crushing it.
  const P = isMobile
    ? { baseW: 430, margin: 62, minSpacing: 96, rootY: 46, tierGap: 126, nodeR: 28, font: 12.5, coreR: 30, labelFont: 8 }
    : { baseW: 820, margin: 120, minSpacing: 150, rootY: 52, tierGap: 138, nodeR: 28, font: 12.5, coreR: 34, labelFont: 10 };

  const layout = useMemo(
    () => computeTreeLayout({ sections, roster, ...P, bottomPad: showTitles ? 26 : 0 }),
    // P is fully derived from isMobile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, roster, isMobile, showTitles],
  );

  // Selected node renders last so its ring sits above neighboring strands.
  const orderedNodes = useMemo(
    () => [...layout.nodes].sort((a, b) => (a.member.id === selectedId) - (b.member.id === selectedId)),
    [layout.nodes, selectedId],
  );

  // Executives (full-hierarchy view) have no Staff desk — their node opens
  // the About Us page where their full profile lives.
  const activate = (m) => {
    if (m.rank === 'executive') navigate('/about');
    else onSelect(m.id);
  };
  const handleKey = (e, m) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate(m);
    }
  };

  // One SVG, two framings: inline (scrolls horizontally when the roster
  // outgrows the container) and fullscreen fit (whole diagram scaled onto
  // one screen — preserveAspectRatio does the work).
  const chartSvg = (fit) => {
    const clipPrefix = fit ? 'webclip-fs' : 'webclip';
    return (
      <svg
        viewBox={`0 0 ${layout.W} ${layout.H}`}
        preserveAspectRatio="xMidYMid meet"
        className={fit ? 'w-full h-full block' : 'w-full h-auto block max-w-[920px] mx-auto'}
        style={!fit && layout.W > P.baseW ? { minWidth: `${Math.round(layout.W * 0.85)}px` } : undefined}
        role="group"
        aria-label="Organization tree — select a teammate"
      >
        <defs>
          <radialGradient id="orgweb-bg" cx="50%" cy="18%" r="80%">
            <stop offset="0%" stopColor="rgba(120,140,255,0.05)" />
            <stop offset="100%" stopColor="rgba(120,140,255,0)" />
          </radialGradient>
          {layout.nodes.map((n) => (
            <clipPath key={n.member.id} id={`${clipPrefix}-${n.member.id}`}>
              <circle cx={n.x} cy={n.y} r={P.nodeR} />
            </clipPath>
          ))}
        </defs>

        <rect x="0" y="0" width={layout.W} height={layout.H} fill="url(#orgweb-bg)" />

        {/* Department lanes */}
        {layout.deptLabels.map((d) => (
          <rect
            key={`lane-${d.id}`}
            x={d.laneX}
            y={d.laneY}
            width={d.laneW}
            height={d.laneH}
            rx="20"
            fill="rgba(255,255,255,0.016)"
            stroke="rgba(255,255,255,0.045)"
          />
        ))}

        {/* Strands */}
        {layout.edges.map((e) => (
          <path
            key={e.id}
            data-edge={e.id}
            d={edgePath(e)}
            fill="none"
            stroke={e.kind === 'reports' ? 'rgba(165,180,252,0.38)' : 'rgba(255,255,255,0.11)'}
            strokeWidth={e.kind === 'reports' ? 1.4 : 1.1}
          />
        ))}

        {/* Department labels (bottom of each lane, wrapped to lane width) */}
        {layout.deptLabels.map((d) => (
          <text
            key={d.id}
            data-dept-label={d.id}
            textAnchor="middle"
            fontSize={P.labelFont}
            letterSpacing="1.4"
            fontWeight="600"
            fill="rgba(255,255,255,0.42)"
          >
            {d.lines.map((line, i) => (
              <tspan key={i} x={d.x} y={d.y - (d.lines.length - 1 - i) * (P.labelFont + 3)}>
                {line}
              </tspan>
            ))}
          </text>
        ))}

        {/* Root — the Defera script logo, small, inside the core ring.
            Clickable: selecting the company deselects whichever teammate
            is active, collapsing the workspace below. */}
        <g
          role="button"
          tabIndex={0}
          aria-label="Defera — clear selection"
          data-org-root
          onClick={() => onSelect(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(null);
            }
          }}
          className="cursor-pointer focus:outline-none"
        >
          <title>Defera — clear selection</title>
          <circle cx={layout.rootX} cy={layout.rootY} r={P.coreR} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.14)" />
          <circle cx={layout.rootX} cy={layout.rootY} r={P.coreR + 5} fill="none" stroke="rgba(255,255,255,0.05)" />
          <image
            href="/defera-transparent.png"
            x={layout.rootX - P.coreR * 0.72}
            y={layout.rootY - P.coreR * 0.42}
            width={P.coreR * 1.44}
            height={P.coreR * 0.84}
            preserveAspectRatio="xMidYMid meet"
            opacity="0.85"
          />
        </g>

        {/* Nodes */}
        {orderedNodes.map((n) => {
          const m = n.member;
          const selected = m.id === selectedId;
          const isManager = m.rank === 'manager';
          const isExecutive = m.rank === 'executive';
          const ring = selected
            ? 'hsl(228, 75%, 72%)'
            : isExecutive
              ? 'rgba(226,232,240,0.55)'
              : isManager
                ? 'rgba(252,211,77,0.55)'
                : 'rgba(255,255,255,0.18)';
          return (
            <g
              key={m.id}
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              aria-label={`${m.name} — ${m.title}`}
              onClick={() => activate(m)}
              onKeyDown={(e) => handleKey(e, m)}
              className="cursor-pointer focus:outline-none"
            >
              <title>{`${m.name} — ${m.title}`}</title>
              {selected ? (
                <circle cx={n.x} cy={n.y} r={P.nodeR + 7} fill="none" stroke="hsl(228, 75%, 72%)" strokeOpacity="0.28" strokeWidth="6" />
              ) : null}
              {/* Monogram fallback drawn UNDER the portrait: if the image
                  404s (portrait-less future teammate), the fallback simply
                  shows through — SVG <image> has no reliable error event. */}
              <circle cx={n.x} cy={n.y} r={P.nodeR} fill={fallbackFill(m.id)} />
              <text x={n.x} y={n.y + 5.5} textAnchor="middle" fontSize={P.nodeR * 0.62} fontWeight="600" fill="#fff">
                {(m.name || '?').charAt(0)}
              </text>
              {m.avatar ? (
                <image
                  href={m.avatar}
                  x={n.x - P.nodeR}
                  y={n.y - P.nodeR}
                  width={P.nodeR * 2}
                  height={P.nodeR * 2}
                  clipPath={`url(#${clipPrefix}-${m.id})`}
                  preserveAspectRatio="xMidYMid slice"
                />
              ) : null}
              <circle cx={n.x} cy={n.y} r={P.nodeR} fill="none" stroke={ring} strokeWidth={selected ? 2.5 : 1.5} />
              {isManager ? (
                <g transform={`translate(${n.x + P.nodeR * 0.72}, ${n.y - P.nodeR * 0.86})`} aria-hidden="true">
                  <circle r="8.5" fill="#0b1020" stroke="rgba(252,211,77,0.5)" />
                  <path d="M-4.5 2.5 L-5.5 -3 L-2 -0.5 L0 -4 L2 -0.5 L5.5 -3 L4.5 2.5 Z" fill="#fcd34d" />
                </g>
              ) : null}
              <text
                x={n.x}
                y={n.y + P.nodeR + 16}
                textAnchor="middle"
                fontSize={P.font}
                fontWeight="600"
                fill={selected ? '#ffffff' : 'rgba(255,255,255,0.78)'}
              >
                {m.name}
              </text>
              {isExecutive || showTitles ? (
                <text
                  x={n.x}
                  y={n.y + P.nodeR + 29}
                  textAnchor="middle"
                  fontSize={P.font - 3.5}
                  fontWeight="500"
                  fill="rgba(255,255,255,0.42)"
                >
                  {diagramTitleLines(m.title).map((line, i) => (
                    <tspan key={i} x={n.x} dy={i === 0 ? 0 : P.font - 2.5}>{line}</tspan>
                  ))}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    );
  };

  // Story (1080×1920) full-hierarchy composition — always the FULL org
  // (leadership + every AI department) regardless of the page toggle, so a
  // phone screenshot carries the whole company. Rank semantics unchanged:
  // CEO → executives → each executive's departments in their own column,
  // manager above specialists inside every card.
  const storyLayout = useMemo(() => {
    if (!storyLeadership.length) return null;
    const orderOf = (id) => { const i = storyDeptOrder.indexOf(id); return i === -1 ? 99 : i; };
    const ordered = sections.filter((s) => !s.noLane).sort((a, b) => orderOf(a.id) - orderOf(b.id));
    return computeStoryLayout({ leadership: storyLeadership, sections: ordered, oversight: storyOversight });
  }, [sections, storyLeadership, storyOversight, storyDeptOrder]);

  const storySvg = () => {
    const L = storyLayout;
    if (!L) return null;

    const person = (n, { ring, crown = false, nameSize, titleSize, titleWrap = 24 }) => (
      <g key={n.member.id}>
        <circle cx={n.x} cy={n.y} r={n.r} fill={fallbackFill(n.member.id)} />
        <text x={n.x} y={n.y + n.r * 0.25} textAnchor="middle" fontSize={n.r * 0.7} fontWeight="600" fill="#fff">
          {(n.member.name || '?').charAt(0)}
        </text>
        {n.member.avatar ? (
          <image
            href={n.member.avatar}
            x={n.x - n.r}
            y={n.y - n.r}
            width={n.r * 2}
            height={n.r * 2}
            clipPath={`url(#storyclip-${n.member.id})`}
            preserveAspectRatio="xMidYMid slice"
          />
        ) : null}
        <circle cx={n.x} cy={n.y} r={n.r} fill="none" stroke={ring} strokeWidth="3" />
        {crown ? (
          <g transform={`translate(${n.x + n.r * 0.72}, ${n.y - n.r * 0.86})`} aria-hidden="true">
            <circle r="13" fill="#0b1020" stroke="rgba(252,211,77,0.5)" />
            <path d="M-6.5 3.5 L-8 -4 L-3 -0.5 L0 -5.5 L3 -0.5 L8 -4 L6.5 3.5 Z" fill="#fcd34d" />
          </g>
        ) : null}
        <text x={n.x} y={n.y + n.r + nameSize + 4} textAnchor="middle" fontSize={nameSize} fontWeight="600" fill="rgba(255,255,255,0.92)">
          {n.member.name}
        </text>
        {diagramTitleLines(n.member.title, titleWrap).map((line, i) => (
          <text key={i} x={n.x} y={n.y + n.r + nameSize + 6 + (i + 1) * (titleSize + 3)} textAnchor="middle" fontSize={titleSize} fontWeight="500" fill="rgba(255,255,255,0.48)">
            {line}
          </text>
        ))}
      </g>
    );
    return (
      <svg
        viewBox={`0 0 ${L.W} ${L.H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full block"
        role="img"
        aria-label="Full hierarchy — story format"
      >
        <defs>
          {[{ ...L.ceoNode }, ...L.execNodes, ...L.nodes].map((n) => (
            <clipPath key={n.member.id} id={`storyclip-${n.member.id}`}>
              <circle cx={n.x} cy={n.y} r={n.r} />
            </clipPath>
          ))}
        </defs>
        <rect x="0" y="0" width={L.W} height={L.H} fill="#050507" />
        {L.edges.map((e, i) => (
          <path key={i} d={edgePath(e)} fill="none" stroke={e.kind === 'spine' ? 'rgba(255,255,255,0.10)' : 'rgba(165,180,252,0.35)'} strokeWidth="2.5" />
        ))}
        {L.cards.map((c) => (
          <g key={c.id}>
            <rect x={c.x} y={c.y} width={c.w} height={c.h} rx="24" fill="rgba(255,255,255,0.022)" stroke="rgba(255,255,255,0.06)" />
            <text x={c.x + c.w / 2} y={c.y + 30} textAnchor="middle" fontSize="16" letterSpacing="2.5" fontWeight="600" fill="rgba(255,255,255,0.42)">
              {`${c.name.toUpperCase()} · ${c.count}`}
            </text>
          </g>
        ))}
        <circle cx={L.logo.x} cy={L.logo.y} r={L.logo.r} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.14)" />
        <circle cx={L.logo.x} cy={L.logo.y} r={L.logo.r + 8} fill="none" stroke="rgba(255,255,255,0.05)" />
        <image href="/defera-transparent.png" x={L.logo.x - L.logo.r * 0.72} y={L.logo.y - L.logo.r * 0.42} width={L.logo.r * 1.44} height={L.logo.r * 0.84} preserveAspectRatio="xMidYMid meet" opacity="0.85" />
        {person(L.ceoNode, { ring: 'rgba(226,232,240,0.6)', nameSize: 30, titleSize: 20 })}
        {L.execNodes.map((n) => person(n, { ring: 'rgba(226,232,240,0.55)', nameSize: 25, titleSize: 17 }))}
        {L.nodes.map((n) => {
          // Denser rows get smaller type AND wrap titles sooner so
          // neighboring titles never touch. Density is per COLUMN — a
          // packed Risk row must not shrink a roomy row elsewhere that
          // happens to share its y.
          const colOf = (x) => Math.floor(x / L.colWidth);
          const rowMates = L.nodes.filter((x) => x.row === n.row && Math.abs(x.y - n.y) < 1 && colOf(x.x) === colOf(n.x)).length;
          return person(n, {
            ring: n.member.rank === 'manager' ? 'rgba(252,211,77,0.55)' : 'rgba(255,255,255,0.2)',
            crown: n.member.rank === 'manager',
            nameSize: rowMates >= 3 ? 17 : 19,
            titleSize: rowMates >= 3 ? 10.5 : 12,
            titleWrap: rowMates >= 3 ? 13 : rowMates === 2 ? 16 : 24,
          });
        })}
      </svg>
    );
  };

  const effectiveFsMode = fsMode
    || (typeof window !== 'undefined' && window.innerHeight > window.innerWidth ? 'story' : 'wide');

  return (
    <>
      <div className="relative rounded-3xl border border-white/[0.06] bg-white/[0.015] overflow-x-auto scrollbar-hide mb-4 sm:mb-5">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="View org chart fullscreen"
          title="Fullscreen"
          className="absolute top-2.5 right-2.5 z-10 inline-flex items-center justify-center h-8 w-8 rounded-full bg-white/[0.06] border border-white/[0.1] text-white/60 hover:text-white hover:bg-white/[0.12] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
        >
          <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
        {chartSvg(false)}
      </div>

      {expanded ? createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Organization chart — fullscreen"
        >
          <div className="flex items-center justify-between gap-2 px-4 pt-[max(env(safe-area-inset-top),12px)] pb-1 flex-shrink-0">
            <img src="/defera-logo-web.png" alt="Defera" className="h-5 w-auto opacity-80" />
            {storyLayout ? (
              <div className="flex items-center rounded-full bg-white/[0.06] border border-white/[0.1] p-0.5" role="group" aria-label="Chart framing">
                {[['wide', 'Wide'], ['story', 'Story']].map(([m, label]) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setFsMode(m)}
                    aria-pressed={effectiveFsMode === m}
                    className={`text-[12px] font-medium px-3 h-8 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 ${
                      effectiveFsMode === m ? 'bg-white/[0.12] text-white' : 'text-white/50 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => { setExpanded(false); setFsMode(null); }}
              aria-label="Close fullscreen"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/[0.08] border border-white/[0.12] text-white/70 hover:text-white hover:bg-white/[0.15] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
            >
              <X className="w-5 h-5" strokeWidth={1.75} />
            </button>
          </div>
          <div className="flex-1 min-h-0 px-2 sm:px-6 pb-[max(env(safe-area-inset-bottom),10px)] flex items-center justify-center">
            {effectiveFsMode === 'story' && storyLayout ? storySvg() : chartSvg(true)}
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function Skeleton({ className = '' }) {
  return <div className={`animate-pulse rounded-lg bg-white/[0.05] ${className}`} aria-hidden="true" />;
}

/** Gradient monogram avatar. size: 'sm' (roster) | 'lg' (workspace). */
function Monogram({ member, size = 'sm' }) {
  const dims = size === 'lg' ? 'w-12 h-12 rounded-2xl text-[18px]' : 'w-10 h-10 rounded-xl text-[15px]';
  return (
    <div
      className={`${dims} bg-gradient-to-br ${gradientFor(member.id)} flex items-center justify-center font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] flex-shrink-0 select-none`}
      aria-hidden="true"
    >
      {(member.name || '?').charAt(0)}
    </div>
  );
}

/**
 * Portrait avatar (AI-generated, /public/staff/<id>.png) with a graceful
 * gradient-monogram fallback when the member has no portrait yet or the
 * file fails to load — new backend-registered staff never break the page.
 */
function Avatar({ member, size = 'sm' }) {
  const [broken, setBroken] = useState(false);
  if (!member.avatar || broken) return <Monogram member={member} size={size} />;
  const dims = size === 'lg' ? 'w-12 h-12 rounded-2xl' : size === 'xs' ? 'w-6 h-6 rounded-full' : 'w-10 h-10 rounded-xl';
  return (
    <img
      src={member.avatar}
      alt={`${member.name} portrait`}
      onError={() => setBroken(true)}
      draggable={false}
      className={`${dims} object-cover flex-shrink-0 select-none border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]`}
    />
  );
}

/** Gold rank chip for department managers. */
function ManagerBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-wider font-semibold text-amber-300 bg-amber-500/10 border border-amber-400/25 px-1.5 py-0.5 rounded-md flex-shrink-0">
      <Crown className="w-2.5 h-2.5" strokeWidth={2} />
      Manager
    </span>
  );
}

function ActiveDot() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-medium text-emerald-300">
      <span className="relative flex w-1.5 h-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-emerald-400" />
      </span>
      Active
    </span>
  );
}

/** Small labeled chip used in the workspace header meta row. */
function MetaChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-white/55 bg-white/[0.04] border border-white/[0.07] rounded-full px-2.5 py-1 whitespace-nowrap">
      <Icon className="w-3 h-3 text-white/35" strokeWidth={1.75} />
      {children}
    </span>
  );
}

function StatTile({ label, value, tone = 'text-white', loading = false }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-3 min-w-0">
      {loading
        ? <Skeleton className="h-6 w-12 mb-1" />
        : <p className={`text-[19px] font-semibold leading-tight tabular-nums truncate ${tone}`}>{value}</p>}
      <p className="text-[10px] uppercase tracking-[0.14em] text-white/40 font-normal mt-1 truncate">{label}</p>
    </div>
  );
}

const VERDICT_META = {
  pass: { label: 'Pass', icon: CheckCircle2, chip: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/25' },
  coach: { label: 'Coaching', icon: GraduationCap, chip: 'text-amber-300 bg-amber-500/10 border-amber-400/25' },
  flag: { label: 'Flagged', icon: AlertTriangle, chip: 'text-rose-300 bg-rose-500/10 border-rose-400/25' },
};

function VerdictBadge({ verdict }) {
  const meta = VERDICT_META[verdict] || VERDICT_META.coach;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-md border ${meta.chip}`}>
      <Icon className="w-3 h-3" strokeWidth={2} />
      {meta.label}
    </span>
  );
}

/** 1–5 score rendered as five dots, tinted by how good the score is. */
function ScoreDots({ label, value }) {
  const v = Number(value) || 0;
  const fill = v <= 2 ? 'bg-rose-400' : v === 3 ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <span className="inline-flex items-center gap-1.5" title={`${label} ${v}/5`} aria-label={`${label} ${v} out of 5`}>
      <span className="text-[10px] uppercase tracking-wider text-white/35 font-normal">{label}</span>
      <span className="flex gap-[3px]">
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`w-[5px] h-[5px] rounded-full ${i <= v ? fill : 'bg-white/15'}`} />
        ))}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------

function RosterCard({ member, selected, onSelect, relationshipLabel }) {
  const isManager = member.rank === 'manager';
  // Up to four headline stats; skeletons while they load.
  const tiles = member.headline?.length ? member.headline.slice(0, 4) : [null, null, null];
  return (
    <button
      type="button"
      onClick={() => onSelect(member.id)}
      aria-pressed={selected}
      className={`snap-start shrink-0 w-[78vw] max-w-[300px] sm:w-auto sm:max-w-none sm:shrink text-left rounded-2xl border p-4 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 ${
        selected
          ? 'bg-white/[0.05] border-brand-400/35 shadow-[0_0_0_1px_rgba(120,140,255,0.12),0_12px_40px_-16px_rgba(0,0,0,0.8)]'
          : isManager
            ? 'bg-white/[0.025] border-amber-400/[0.18] hover:bg-white/[0.045] hover:border-amber-400/30 active:bg-white/[0.05]'
            : 'bg-white/[0.02] border-white/[0.07] hover:bg-white/[0.04] hover:border-white/[0.12] active:bg-white/[0.05]'
      }`}
    >
      <div className="flex items-start gap-3">
        <Avatar member={member} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-white leading-tight truncate">{member.name}</span>
            {isManager ? <ManagerBadge /> : null}
            <span className="ml-auto"><ActiveDot /></span>
          </div>
          <p className="text-[11.5px] text-brand-300/90 font-medium mt-0.5 truncate">{member.title}</p>
          {relationshipLabel ? (
            <p className="flex items-center gap-1 text-[10.5px] text-white/40 mt-1 truncate">
              <CornerDownRight className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2} />
              {relationshipLabel}
            </p>
          ) : null}
        </div>
      </div>

      <p className="text-[12px] text-white/50 leading-relaxed mt-3 line-clamp-2">{member.summary}</p>

      {/* Generic headline stats — renders whatever the server sends for
          this member, so future staff get live numbers for free. Four sit
          two by two so their labels still fit. */}
      <div className={`grid ${tiles.length > 3 ? 'grid-cols-2' : 'grid-cols-3'} gap-2 mt-3 pt-3 border-t border-white/[0.06]`}>
        {tiles.map((h, i) => (
          <div key={h ? h.label : i} className="min-w-0">
            {h ? (
              <>
                <p className="text-[14px] font-semibold text-white leading-tight tabular-nums truncate">{h.value}</p>
                <p className="text-[9.5px] uppercase tracking-[0.12em] text-white/35 font-medium truncate mt-0.5">{h.label}</p>
              </>
            ) : (
              <>
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-2 w-12 mt-1.5" />
              </>
            )}
          </div>
        ))}
      </div>

      <div className={`flex items-center gap-1 text-[11px] font-normal mt-3 transition-colors ${selected ? 'text-brand-300' : 'text-white/35'}`}>
        {selected ? 'Viewing desk' : 'View desk'}
        <ChevronRight className="w-3 h-3" strokeWidth={2} />
      </div>
    </button>
  );
}

function RosterSkeletonCard() {
  return (
    <div className="snap-start shrink-0 w-[78vw] max-w-[300px] sm:w-auto sm:max-w-none sm:shrink rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4">
      <div className="flex items-start gap-3">
        <Skeleton className="w-10 h-10 rounded-xl" />
        <div className="flex-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-32 mt-2" />
        </div>
      </div>
      <Skeleton className="h-3 w-full mt-4" />
      <Skeleton className="h-3 w-3/4 mt-2" />
      <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-white/[0.06]">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-8" />)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stephanie's desk — the review feed over Tommy's replies
// ---------------------------------------------------------------------------

const REVIEW_PAGE_SIZE = 25;

function ReviewRow({ review }) {
  const [open, setOpen] = useState(false);
  const customer = review.thread?.user_first_name
    || (review.thread?.user_email ? review.thread.user_email.split('@')[0] : 'Customer');
  const issues = Array.isArray(review.issues) ? review.issues : [];

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full text-left px-3.5 sm:px-4 py-3.5 min-h-[56px] transition focus:outline-none focus-visible:bg-white/[0.05] ${
          open ? 'bg-white/[0.035]' : 'hover:bg-white/[0.025] active:bg-white/[0.05]'
        }`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <VerdictBadge verdict={review.verdict} />
          <span className="text-[13.5px] font-medium text-white truncate">{customer}</span>
          {review.booking_reference ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300/75">
              <Plane className="w-3 h-3" strokeWidth={1.75} />
              {review.booking_reference}
            </span>
          ) : null}
          <span className="ml-auto flex items-center gap-2 flex-shrink-0">
            <span className="text-[11px] text-white/40">{formatRelativeTime(review.created_at)}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-white/35 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              strokeWidth={1.75}
            />
          </span>
        </div>

        <p className="text-[12.5px] text-white/60 mt-1.5 line-clamp-2 leading-snug">{review.assistant_message}</p>

        <div className="flex items-center gap-3.5 mt-2 flex-wrap">
          <ScoreDots label="Accuracy" value={review.accuracy} />
          <ScoreDots label="Tone" value={review.tone} />
          {issues.length > 0 ? (
            <span className="text-[10.5px] text-white/40">{issues.length} issue{issues.length === 1 ? '' : 's'}</span>
          ) : null}
          {review.verdict === 'flag' ? (
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
              review.correction_delivered_at
                ? 'text-emerald-300/85 bg-emerald-500/10'
                : 'text-white/45 bg-white/[0.06]'
            }`}>
              {review.correction_delivered_at ? 'Correction delivered' : 'Correction pending'}
            </span>
          ) : null}
          <span className="text-[10.5px] text-white/30 uppercase tracking-wider ml-auto">
            {review.trigger === 'audit' ? 'Audit' : 'Live'}
          </span>
        </div>
      </button>

      {open ? (
        <div className="px-3.5 sm:px-4 pb-4 space-y-2.5">
          {review.user_message ? (
            <div className="rounded-xl bg-white/[0.025] border-l-2 border-white/15 px-3.5 py-3">
              <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40 mb-1">Customer asked</p>
              <p className="text-[13px] text-white/80 whitespace-pre-wrap leading-relaxed">{review.user_message}</p>
            </div>
          ) : null}

          <div className="rounded-xl bg-white/[0.025] border-l-2 border-brand-400/50 px-3.5 py-3">
            <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-brand-300/85 mb-1">Tommy replied</p>
            <p className="text-[13px] text-white/80 whitespace-pre-wrap leading-relaxed">{review.assistant_message}</p>
          </div>

          {issues.length > 0 ? (
            <div className="rounded-xl bg-white/[0.025] border-l-2 border-white/15 px-3.5 py-3 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40">Findings</p>
              {issues.map((issue, i) => (
                <div key={i} className="text-[12.5px] leading-relaxed">
                  <span className={`inline-block mr-1.5 align-middle text-[9.5px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                    issue.severity === 'major'
                      ? 'text-rose-300 bg-rose-500/10'
                      : 'text-white/55 bg-white/[0.06]'
                  }`}>
                    {issue.type}{issue.severity === 'major' ? ' · major' : ''}
                  </span>
                  <span className="text-white/75">{issue.summary}</span>
                  {issue.evidence ? (
                    <span className="block text-white/40 italic mt-0.5">“{issue.evidence}”</span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {review.coaching_note ? (
            <div className="rounded-xl bg-amber-500/[0.05] border-l-2 border-amber-400/50 px-3.5 py-3">
              <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-amber-300/85 mb-1">Coaching note to Tommy</p>
              <p className="text-[13px] text-white/80 whitespace-pre-wrap leading-relaxed">{review.coaching_note}</p>
            </div>
          ) : null}

          {review.corrected_reply ? (
            <div className="rounded-xl bg-emerald-500/[0.05] border-l-2 border-emerald-400/50 px-3.5 py-3">
              <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-emerald-300/85 mb-1">
                What Tommy should have said
              </p>
              <p className="text-[13px] text-white/80 whitespace-pre-wrap leading-relaxed">{review.corrected_reply}</p>
              <p className="text-[11px] text-white/40 mt-2">
                {review.correction_delivered_at
                  ? 'Delivered — Tommy corrected the record with the customer on a later turn.'
                  : 'Pending — will be woven into Tommy’s next reply on this thread.'}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * The daily report — the queue Stephanie files into (migration 095).
 *
 * When she triages something as "needs a human, but not this second", she
 * writes it up and it lands here instead of a live concierge's phone. Each
 * row is a promise Tommy already made to a customer in chat: an answer
 * within 24 hours, visible to them as a card on their trip page.
 *
 * Which makes this list the load-bearing part of the whole design. The
 * reason it's acceptable for Tommy to say "our team will get back to you"
 * instead of handing over a human is that someone actually works this. If
 * nobody does, we haven't reduced concierge exposure — we've just moved
 * the customer's disappointment 24 hours into the future and made them
 * chase it. Hence: overdue is loud, and ordering is by promise due, not by
 * recency.
 */
function DailyReport({ buildAuthHeader }) {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('outstanding');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (status = statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries?status=${encodeURIComponent(status)}`, {
        headers: { Authorization: authHeader },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setInquiries(data.inquiries || []);
      setStats(data.stats || null);
    } catch (err) {
      setError(err?.message || 'Failed to load the daily report.');
    } finally {
      setLoading(false);
    }
  }, [buildAuthHeader, statusFilter]);

  useEffect(() => { load('outstanding'); }, [load]);

  const resolve = useCallback(async (id) => {
    const note = noteDraft.trim();
    // The server rejects an empty note too — this is just the fast path.
    // Resolving with nothing to say would flip the customer's card to
    // "Answered" and show them a blank, which is worse than leaving it open.
    if (!note) return;
    setSaving(true);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(id)}/resolve`, {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolutionNote: note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setExpandedId(null);
      setNoteDraft('');
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to resolve that inquiry.');
    } finally {
      setSaving(false);
    }
  }, [buildAuthHeader, noteDraft, load]);

  const isOverdue = (inq) => inq.due_by
    && new Date(inq.due_by).getTime() < Date.now()
    && ['open', 'in_progress'].includes(inq.status);

  const dueLabel = (inq) => {
    if (!inq.due_by) return '';
    const ms = new Date(inq.due_by).getTime() - Date.now();
    const hrs = Math.round(Math.abs(ms) / 3600000);
    if (ms < 0) return `${hrs}h overdue`;
    return `due in ${hrs}h`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Inbox className="w-4 h-4 text-amber-300 flex-shrink-0" strokeWidth={1.75} />
          <h3 className="text-[14.5px] font-semibold tracking-tight truncate">Daily report</h3>
          <Link
            to="/staff/inquiries"
            className="inline-flex items-center gap-0.5 text-[11px] text-amber-200/70 hover:text-amber-200 transition flex-shrink-0"
            title="Open the full inquiries desk — split view with each customer's chat"
          >
            Open desk
            <ArrowUpRight className="w-3 h-3" strokeWidth={1.75} />
          </Link>
          {stats?.overdue ? (
            <span className="text-[10px] uppercase tracking-wider font-medium text-red-200 bg-red-500/15 border border-red-400/25 px-1.5 py-0.5 rounded">
              {stats.overdue} overdue
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {['outstanding', 'overdue', 'resolved'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setStatusFilter(s); load(s); }}
              className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors capitalize ${
                statusFilter === s
                  ? 'bg-white/[0.10] border-white/20 text-white'
                  : 'bg-white/[0.03] border-white/[0.08] text-white/55 hover:text-white'
              }`}
            >
              {s}
            </button>
          ))}
          <button
            type="button"
            onClick={() => load()}
            className="text-[11.5px] px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] text-white/55 hover:text-white transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {stats ? (
        <div className="text-[11.5px] text-white/40">
          {stats.outstanding} outstanding · {stats.resolved} resolved
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200/90">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="text-[12.5px] text-white/40 py-4">Loading…</div>
      ) : inquiries.length === 0 ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-6 text-center text-[12.5px] text-white/40">
          {statusFilter === 'resolved'
            ? 'Nothing resolved yet.'
            : 'Nothing outstanding — the queue is clear.'}
        </div>
      ) : (
        <div className="space-y-2">
          {inquiries.map((inq) => {
            const overdue = isOverdue(inq);
            const open = expandedId === inq.id;
            return (
              <div
                key={inq.id}
                className={`rounded-xl border overflow-hidden ${
                  overdue
                    ? 'border-red-400/25 bg-red-500/[0.05]'
                    : 'border-white/[0.07] bg-white/[0.025]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setExpandedId(open ? null : inq.id);
                    setNoteDraft(inq.resolution_note || '');
                  }}
                  className="w-full text-left px-3.5 py-2.5 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-wider font-medium text-amber-200/85 bg-amber-500/12 border border-amber-400/20 px-1.5 py-0.5 rounded">
                      {inq.category || 'other'}
                    </span>
                    {inq.status === 'resolved' ? (
                      <span className="text-[10px] uppercase tracking-wider font-medium text-emerald-300/90">Resolved</span>
                    ) : (
                      <span className={`text-[10px] uppercase tracking-wider font-medium ${overdue ? 'text-red-300' : 'text-white/40'}`}>
                        {dueLabel(inq)}
                      </span>
                    )}
                    {inq.booking_reference ? (
                      <span className="text-[10px] text-emerald-300/80">{inq.booking_reference}</span>
                    ) : null}
                    <span className="ml-auto text-[10.5px] text-white/35 truncate max-w-[45%]">
                      {inq.user_first_name || inq.user_email || 'Customer'}
                    </span>
                    <VipBadge userId={inq.user_id || null} />
                  </div>
                  <div className="text-[13px] font-medium text-white/90 leading-snug">
                    {inq.title || inq.customer_question}
                  </div>
                </button>

                {open ? (
                  <div className="px-3.5 pb-3.5 pt-1 space-y-2.5 border-t border-white/[0.05]">
                    {/* Stephanie's write-up. INTERNAL — this is the whole
                        reason the queue is cheaper than a phone call, and
                        the reason the customer's payload omits it. */}
                    <div className="rounded-lg bg-black/25 px-3 py-2 space-y-1.5">
                      {inq.title ? (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-medium text-white/35 mb-0.5">
                            Customer&apos;s message
                          </div>
                          <div className="text-[12.5px] text-white/80 leading-relaxed">{inq.customer_question}</div>
                        </div>
                      ) : null}
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-medium text-white/35 mb-0.5">
                          Stephanie&apos;s summary
                        </div>
                        <div className="text-[12.5px] text-white/80 leading-relaxed">{inq.summary || '—'}</div>
                      </div>
                      {inq.recommendation ? (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-medium text-white/35 mb-0.5">
                            Her recommendation
                          </div>
                          <div className="text-[12.5px] text-white/80 leading-relaxed">{inq.recommendation}</div>
                        </div>
                      ) : null}
                      <div className="text-[10.5px] text-white/30 pt-0.5">
                        {inq.user_email}
                        {inq.consult_id ? ' · consult on file' : ''}
                      </div>
                    </div>

                    {inq.status === 'resolved' ? (
                      <div className="rounded-lg border border-emerald-400/20 bg-emerald-500/[0.06] px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider font-medium text-emerald-300/80 mb-0.5">
                          Sent to the customer
                        </div>
                        <div className="text-[12.5px] text-white/85 whitespace-pre-wrap">{inq.resolution_note}</div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <label htmlFor={`note-${inq.id}`} className="block text-[11px] text-white/45">
                          Your answer — the customer reads this verbatim on their trip page.
                        </label>
                        <textarea
                          id={`note-${inq.id}`}
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          rows={4}
                          placeholder="Write directly to the customer…"
                          className="w-full rounded-lg bg-black/30 border border-white/[0.10] px-3 py-2 text-base md:text-[13px] text-white/90 placeholder:text-white/25 focus:outline-none focus:border-white/25 resize-y"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => resolve(inq.id)}
                            disabled={saving || !noteDraft.trim()}
                            className="text-[12px] px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {saving ? 'Sending…' : 'Send & resolve'}
                          </button>
                          <span className="text-[11px] text-white/30">
                            Appears on their trip page immediately and is emailed to them.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StephanieDesk({ buildAuthHeader, onDataChanged }) {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [verdictFilter, setVerdictFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  // Guards against out-of-order responses when the admin taps filters fast.
  const fetchToken = useRef(0);

  const loadReviews = useCallback(async ({ filter = verdictFilter, append = false, offset = 0 } = {}) => {
    const token = fetchToken.current + 1;
    fetchToken.current = token;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const params = new URLSearchParams();
      if (filter) params.set('verdict', filter);
      params.set('limit', String(REVIEW_PAGE_SIZE));
      params.set('offset', String(offset));
      const res = await fetch(`/api/admin/staff/stephanie/reviews?${params.toString()}`, {
        headers: { Authorization: authHeader },
      });
      if (token !== fetchToken.current) return;
      if (res.status === 401 || res.status === 403) throw new Error("You don't have access to Stephanie's reviews.");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setReviews((prev) => (append ? [...prev, ...(data.reviews || [])] : (data.reviews || [])));
      setStats(data.stats || null);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      if (token !== fetchToken.current) return;
      setError(err?.message || "Failed to load Stephanie's reviews.");
    } finally {
      if (token === fetchToken.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [buildAuthHeader, verdictFilter]);

  useEffect(() => {
    loadReviews({ filter: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilter = useCallback((next) => {
    setVerdictFilter(next);
    loadReviews({ filter: next });
  }, [loadReviews]);

  const runAudit = useCallback(async () => {
    setAuditRunning(true);
    setAuditResult(null);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/staff/stephanie/audit', {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 10 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Audit failed (${res.status}).`);
      setAuditResult(data);
      await loadReviews();
      onDataChanged?.();
    } catch (err) {
      setError(err?.message || 'Audit failed.');
    } finally {
      setAuditRunning(false);
    }
  }, [buildAuthHeader, loadReviews, onDataChanged]);

  const passRate = useMemo(() => {
    if (!stats?.total) return null;
    return Math.round((stats.pass / stats.total) * 100);
  }, [stats]);

  const filters = [
    { id: '', label: 'All', count: stats?.total },
    { id: 'flag', label: 'Flagged', count: stats?.flag },
    { id: 'coach', label: 'Coaching', count: stats?.coach },
    { id: 'pass', label: 'Pass', count: stats?.pass },
  ];

  return (
    <div className="space-y-6">
      {/* The daily report sits ABOVE the review desk deliberately. Reviews
          are retrospective QA — useful, but nobody is waiting on them. The
          report is a worklist of live commitments we made to real
          customers, each on a 24h clock. It goes first because it's the
          only thing on this page that goes stale. */}
      <DailyReport buildAuthHeader={buildAuthHeader} />

      <div className="space-y-3">
      {/* Action row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardCheck className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
          <h3 className="text-[14.5px] font-semibold tracking-tight truncate">Review desk</h3>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => loadReviews()}
            className="inline-flex items-center justify-center text-[12.5px] font-medium text-white/65 hover:text-white w-10 h-10 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
            aria-label="Refresh reviews"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            type="button"
            onClick={runAudit}
            disabled={auditRunning}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-white px-3.5 h-10 sm:h-auto sm:py-1.5 rounded-full bg-brand-600 active:bg-brand-700 hover:bg-brand-500 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          >
            <Sparkles className={`w-3.5 h-3.5 ${auditRunning ? 'animate-pulse' : ''}`} strokeWidth={2} />
            {auditRunning ? 'Reviewing…' : 'Run audit'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <StatTile label="Reviews" value={stats?.total ?? '—'} loading={loading && !stats} />
        <StatTile label="Pass rate" value={passRate == null ? '—' : `${passRate}%`} loading={loading && !stats} />
        <StatTile label="Flagged" value={stats?.flag ?? '—'} tone="text-rose-300" loading={loading && !stats} />
        <StatTile label="Coaching" value={stats?.coach ?? '—'} tone="text-amber-300" loading={loading && !stats} />
        <StatTile label="Corrections delivered" value={stats?.corrections_delivered ?? '—'} tone="text-emerald-300" loading={loading && !stats} />
      </div>

      {/* Audit result strip */}
      {auditResult ? (
        <div className="rounded-xl bg-brand-500/[0.08] border border-brand-400/20 px-3.5 py-2.5 text-[12.5px] text-white/80 leading-relaxed">
          Stephanie reviewed <strong>{auditResult.reviewed}</strong> Tommy repl{auditResult.reviewed === 1 ? 'y' : 'ies'}
          {auditResult.verdicts
            ? <> — {auditResult.verdicts.pass} pass · {auditResult.verdicts.coach} coaching · {auditResult.verdicts.flag} flagged</>
            : null}
          {auditResult.remaining_unreviewed > 0
            ? <>. About {auditResult.remaining_unreviewed} older turn{auditResult.remaining_unreviewed === 1 ? '' : 's'} still unreviewed — run it again to keep going.</>
            : auditResult.reviewed === 0
              ? <>. Everything recent has already been reviewed.</>
              : null}
        </div>
      ) : null}

      {/* Filter pills — horizontal scroll on tiny screens */}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => applyFilter(f.id)}
            aria-pressed={verdictFilter === f.id}
            className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 h-9 rounded-full border transition flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 ${
              verdictFilter === f.id
                ? 'bg-brand-500/15 text-brand-200 border-brand-400/30'
                : 'bg-white/[0.03] text-white/60 border-white/[0.08] hover:text-white hover:bg-white/[0.06]'
            }`}
          >
            {f.label}
            {typeof f.count === 'number' ? (
              <span className={`text-[10.5px] tabular-nums ${verdictFilter === f.id ? 'text-brand-200/80' : 'text-white/35'}`}>
                {f.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Review feed */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        {loading && reviews.length === 0 ? (
          <div className="divide-y divide-white/[0.05]">
            {[0, 1, 2].map((i) => (
              <div key={i} className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-12 ml-auto" />
                </div>
                <Skeleton className="h-3 w-full mt-3" />
                <Skeleton className="h-3 w-2/3 mt-2" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-[13px] text-rose-300 mb-3">{error}</p>
            <button
              type="button"
              onClick={() => loadReviews()}
              className="text-[12.5px] font-medium text-white px-4 py-2 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] transition"
            >
              Try again
            </button>
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-white/45 max-w-md mx-auto">
            <MessageSquare className="w-7 h-7 mx-auto text-white/20 mb-3" strokeWidth={1.5} />
            {verdictFilter
              ? 'No reviews match this filter yet.'
              : 'No reviews on file yet. Stephanie automatically reviews every new Tommy reply — or tap "Run audit" to have her review recent conversations right now.'}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-white/[0.05]">
              {reviews.map((r) => <ReviewRow key={r.id} review={r} />)}
            </ul>
            {reviews.length < total ? (
              <div className="p-3 border-t border-white/[0.05]">
                <button
                  type="button"
                  onClick={() => loadReviews({ append: true, offset: reviews.length })}
                  disabled={loadingMore}
                  className="w-full text-[12.5px] font-medium text-white/70 hover:text-white py-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.07] transition disabled:opacity-60"
                >
                  {loadingMore ? 'Loading…' : `Load more (${(total - reviews.length).toLocaleString()} remaining)`}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared desk plumbing
// ---------------------------------------------------------------------------

/**
 * Fetch a member's desk payload from /api/admin/staff/:id/desk.
 * Members whose desks are fully client-side never call this.
 */
function useDesk(memberId, buildAuthHeader) {
  const [desk, setDesk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/staff/${encodeURIComponent(memberId)}/desk`, {
        headers: { Authorization: authHeader },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setDesk(data.desk || null);
    } catch (err) {
      setError(err?.message || 'Failed to load desk.');
    } finally {
      setLoading(false);
    }
  }, [memberId, buildAuthHeader]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { desk, loading, error, reload };
}

/** Deep-link card into the member's full admin surface. */
function LinkCard({ to, title, desc, accent = false }) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-between rounded-2xl border transition p-4 group ${
        accent
          ? 'bg-brand-500/[0.07] border-brand-400/20 hover:border-brand-400/40 hover:bg-brand-500/[0.1]'
          : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.14] hover:bg-white/[0.04]'
      }`}
    >
      <div className="min-w-0">
        <p className="text-[13px] text-white font-medium">{title}</p>
        <p className="text-[12px] text-white/45 mt-0.5">{desc}</p>
      </div>
      <ArrowUpRight
        className={`w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${accent ? 'text-brand-300' : 'text-white/40'}`}
        strokeWidth={2}
      />
    </Link>
  );
}

/** Section heading used at the top of each desk. */
function DeskHeading({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
      <h3 className="text-[14.5px] font-semibold tracking-tight">{children}</h3>
    </div>
  );
}

function DeskError({ error, onRetry }) {
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-6 text-center">
      <p className="text-[13px] text-rose-300 mb-3">{error}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-[12.5px] font-medium text-white px-4 py-2 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] transition"
      >
        Try again
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tommy's desk — performance snapshot + jump-offs
// ---------------------------------------------------------------------------

function TommyDesk({ member, onSelectMember }) {
  const headline = member.headline || [];
  const get = (label) => headline.find((h) => h.label === label)?.value;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
        <h3 className="text-[14.5px] font-semibold tracking-tight">Performance snapshot</h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatTile label="Conversations" value={get('Conversations') ?? '—'} loading={!headline.length} />
        <StatTile label="Handoffs to humans" value={get('Handoffs') ?? '—'} loading={!headline.length} />
        <StatTile label="Quality pass rate" value={get('Pass rate') ?? '—'} tone="text-emerald-300" loading={!headline.length} />
      </div>

      <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-white/80 font-medium">Every reply is reviewed by Stephanie.</p>
          <p className="text-[12px] text-white/45 leading-relaxed mt-0.5">
            His quality pass rate above comes from her review desk — flagged replies generate a correction Tommy delivers on his next turn.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onSelectMember('stephanie')}
          className="inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-white px-3.5 h-10 sm:h-9 rounded-full bg-white/[0.05] hover:bg-white/[0.09] border border-white/[0.09] transition flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
        >
          Open Stephanie&apos;s desk
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>

      <LinkCard
        to="/support"
        title="Concierge inbox"
        desc="Read Tommy's live customer conversations, or chat with him yourself."
        accent
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vera's desk — risk operations
// ---------------------------------------------------------------------------

function VeraDesk({ buildAuthHeader }) {
  const { desk, loading, error, reload } = useDesk('vera', buildAuthHeader);

  if (error) return <DeskError error={error} onRetry={reload} />;

  return (
    <div className="space-y-3">
      <DeskHeading icon={ShieldAlert}>Risk operations</DeskHeading>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <StatTile label="Decisions logged" value={desk?.decisions_logged ?? '—'} loading={loading} />
        <StatTile label="Outcomes tracked" value={desk?.outcomes_tracked ?? '—'} loading={loading} />
        <StatTile label="Defaults" value={desk?.defaults ?? '—'} tone="text-rose-300" loading={loading} />
        <StatTile label="Chargebacks" value={desk?.chargebacks ?? '—'} tone="text-rose-300" loading={loading} />
        <StatTile label="Manual overrides" value={desk?.manual_overrides ?? '—'} tone="text-amber-300" loading={loading} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <LinkCard
          to="/risk-review"
          title="Risk Review"
          desc="Her live portfolio scan — per-trip risk scores, expected loss, and booking-timing calls."
          accent
        />
        <LinkCard
          to="/riskinsights"
          title="Risk Insights"
          desc="Stripe Radar signals: charge success rate, failure codes, and risk levels."
        />
        <LinkCard
          to="/model-performance"
          title="Model Performance — Vera's scorecard"
          desc="Every call she makes is tracked to a won/lost outcome: win rate, net PnL, default and chargeback rates."
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marcus's desk — latest portfolio snapshot
// ---------------------------------------------------------------------------

function MarcusDesk({ buildAuthHeader }) {
  const { desk, loading, error, reload } = useDesk('marcus', buildAuthHeader);

  if (error) return <DeskError error={error} onRetry={reload} />;
  const snap = desk?.snapshot;

  return (
    <div className="space-y-3">
      <DeskHeading icon={Gauge}>Portfolio snapshot</DeskHeading>

      {!loading && !snap ? (
        <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-6 text-center">
          <p className="text-[13px] text-white/70 font-normal">No snapshot on file yet.</p>
          <p className="text-[12px] text-white/40 mt-1">Generate and save one from Portfolio Intelligence below.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatTile
              label={`Health score${snap?.status ? ` · ${snap.status}` : ''}`}
              value={snap?.health_score ?? '—'}
              tone={
                snap?.health_score == null ? 'text-white'
                  : snap.health_score >= 70 ? 'text-emerald-300'
                    : snap.health_score >= 50 ? 'text-amber-300' : 'text-rose-300'
              }
              loading={loading}
            />
            <StatTile
              label="Liquidity coverage 14d"
              value={snap?.liquidity_coverage_ratio_14d != null ? `${Number(snap.liquidity_coverage_ratio_14d).toFixed(2)}×` : '—'}
              loading={loading}
            />
            <StatTile
              label="Critical alerts"
              value={snap?.critical_alerts ?? '—'}
              tone={snap?.critical_alerts > 0 ? 'text-rose-300' : 'text-emerald-300'}
              loading={loading}
            />
            <StatTile label="Trips covered" value={snap?.trip_count ?? '—'} loading={loading} />
          </div>
          {snap?.generated_at ? (
            <p className="text-[11px] text-white/35">Snapshot generated {formatRelativeTime(snap.generated_at)}.</p>
          ) : null}
        </>
      )}

      <LinkCard
        to="/portfolio-intelligence"
        title="Portfolio Intelligence"
        desc="His full workspace — exposure breakdowns, stress tests, liquidity planning, and snapshot history."
        accent
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Penny's desk — payment cron + past-due health
// ---------------------------------------------------------------------------

function PennyDesk({ buildAuthHeader }) {
  const { desk, loading, error, reload } = useDesk('penny', buildAuthHeader);

  if (error) return <DeskError error={error} onRetry={reload} />;
  const ladder = desk?.escalation;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <DeskHeading icon={Clock3}>Payment operations</DeskHeading>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center justify-center text-[12.5px] font-medium text-white/65 hover:text-white w-10 h-10 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          aria-label="Refresh payment health"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
          <span className="hidden sm:inline">Health check</span>
        </button>
      </div>

      {/* The alarm tile: due installments the charge cron hasn't processed.
          Should sit at ~0 shortly after the 14:00 UTC run — anything that
          stays elevated means charges aren't being triggered. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile
          label="Due & unprocessed"
          value={desk?.due_unprocessed ?? '—'}
          tone={desk?.due_unprocessed > 0 ? 'text-rose-300' : 'text-emerald-300'}
          loading={loading}
        />
        <StatTile
          label="Retrying"
          value={desk?.retrying ?? '—'}
          tone={desk?.retrying > 0 ? 'text-amber-300' : 'text-white'}
          loading={loading}
        />
        <StatTile label="Charged (7d)" value={desk?.charged_7d ?? '—'} tone="text-emerald-300" loading={loading} />
        <StatTile
          label="Last successful charge"
          value={desk?.last_charge_at ? formatRelativeTime(desk.last_charge_at) : '—'}
          loading={loading}
        />
      </div>

      {/* Escalation ladder */}
      <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4">
        <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40 mb-2.5">Escalation ladder — open episodes</p>
        <div className="flex items-center gap-2 flex-wrap text-[12.5px]">
          <span className="inline-flex items-center gap-1.5 text-white/80">
            <span className="text-amber-300 font-semibold tabular-nums">{loading ? '…' : (ladder?.open_warnings ?? '—')}</span>
            warnings sent
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-white/25" strokeWidth={2} />
          <span className="inline-flex items-center gap-1.5 text-white/80">
            <span className="text-orange-300 font-semibold tabular-nums">{loading ? '…' : (ladder?.final_notices_pending ?? '—')}</span>
            final notices pending
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-white/25" strokeWidth={2} />
          <span className="inline-flex items-center gap-1.5 text-white/80">
            <span className="text-rose-300 font-semibold tabular-nums">{loading ? '…' : (ladder?.auto_defaults_total ?? '—')}</span>
            auto-defaults to date
          </span>
        </div>
        <p className="text-[11px] text-white/35 mt-2 leading-relaxed">
          A customer two installments behind gets a warning; still behind two days later, a final notice; still behind a week after that, the booking auto-defaults and a collections case opens.
        </p>
      </div>

      {/* Cron timetable */}
      {desk?.crons?.length ? (
        <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
          <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40 px-4 pt-3.5 pb-1">Daily cadence</p>
          <ul className="divide-y divide-white/[0.04]">
            {desk.crons.map((c) => (
              <li key={c.path} className="px-4 py-2.5 flex items-baseline gap-3">
                <span className="text-[11.5px] font-medium text-brand-300/90 tabular-nums whitespace-nowrap w-[110px] flex-shrink-0">{c.schedule}</span>
                <span className="text-[12.5px] text-white/70 leading-snug">{c.purpose}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <LinkCard
          to="/past-due"
          title="Past Due"
          desc="Every past-due plan with live Stripe + NMI detail, exposure sizing, and outreach stamps."
          accent
        />
        <LinkCard
          to="/activity?view=upcoming"
          title="Upcoming charges"
          desc="The NMI cron monitoring board — spot charges about to fail before they do."
        />
        <LinkCard
          to="/collections"
          title="Collections"
          desc="Defaulted bookings handed to collections — the end of Penny's escalation ladder."
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audrey's desk — findings ledger + cron heartbeat board
// ---------------------------------------------------------------------------

const AUDREY_SEVERITY_STYLES = {
  critical: { chip: 'bg-rose-500/15 text-rose-300 border-rose-500/25', label: 'Critical' },
  warning: { chip: 'bg-amber-500/15 text-amber-300 border-amber-500/25', label: 'Warning' },
  info: { chip: 'bg-sky-500/15 text-sky-300 border-sky-500/25', label: 'Info' },
};

function AudreyDesk({ buildAuthHeader }) {
  const { desk, loading, error, reload } = useDesk('audrey', buildAuthHeader);

  if (error) return <DeskError error={error} onRetry={reload} />;
  const counts = desk?.counts;
  const findings = desk?.open_findings || [];
  const lastRun = desk?.last_run;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <DeskHeading icon={ClipboardCheck}>Operations audit</DeskHeading>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center justify-center text-[12.5px] font-medium text-white/65 hover:text-white w-10 h-10 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          aria-label="Refresh audit desk"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* The verdict tiles: open criticals is Audrey's alarm bell. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile
          label="Open criticals"
          value={counts?.critical ?? '—'}
          tone={counts?.critical > 0 ? 'text-rose-300' : 'text-emerald-300'}
          loading={loading}
        />
        <StatTile
          label="Open warnings"
          value={counts?.warning ?? '—'}
          tone={counts?.warning > 0 ? 'text-amber-300' : 'text-white'}
          loading={loading}
        />
        <StatTile label="Info notes" value={counts?.info ?? '—'} loading={loading} />
        <StatTile
          label="Last audit"
          value={lastRun?.started_at ? formatRelativeTime(lastRun.started_at) : '—'}
          tone={lastRun && lastRun.ok === false ? 'text-rose-300' : 'text-white'}
          loading={loading}
        />
      </div>

      {/* Open findings ledger */}
      <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
        <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40 px-4 pt-3.5 pb-1">Open findings</p>
        {!loading && findings.length === 0 ? (
          <div className="px-4 pb-4 pt-1 flex items-center gap-2 text-[12.5px] text-white/60">
            <CheckCircle2 className="w-4 h-4 text-emerald-300" strokeWidth={1.75} />
            {lastRun
              ? 'Nothing open — every check in the last sweep came back clean.'
              : 'No audit sweeps recorded yet — the hourly cron starts filing after the next deploy.'}
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {findings.slice(0, 25).map((f) => {
              const style = AUDREY_SEVERITY_STYLES[f.severity] || AUDREY_SEVERITY_STYLES.info;
              return (
                <li key={f.id} className="px-4 py-2.5">
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-medium uppercase tracking-wide flex-shrink-0 ${style.chip}`}>
                      {style.label}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12.5px] text-white/80 leading-snug">{f.title}</p>
                      <p className="text-[11px] text-white/35 mt-0.5">
                        {f.check_key} · first seen {formatRelativeTime(f.first_seen_at)}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {findings.length > 25 ? (
          <p className="px-4 pb-3 text-[11px] text-white/35">…and {findings.length - 25} more.</p>
        ) : null}
      </div>

      {/* Cron heartbeat board — did every one of Penny's crons actually run? */}
      {desk?.cron_board?.length ? (
        <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
          <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40 px-4 pt-3.5 pb-1">Cron heartbeats</p>
          <ul className="divide-y divide-white/[0.04]">
            {desk.cron_board.map((c) => (
              <li key={c.job} className="px-4 py-2.5 flex items-center gap-3">
                {c.healthy ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0" strokeWidth={1.75} />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-300 flex-shrink-0" strokeWidth={1.75} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] text-white/80 leading-snug truncate">{c.purpose}</p>
                  <p className="text-[11px] text-white/35">{c.schedule}</p>
                </div>
                <span className="text-[11.5px] text-white/45 tabular-nums whitespace-nowrap">
                  {c.last_ok_at ? formatRelativeTime(c.last_ok_at) : 'no runs yet'}
                </span>
              </li>
            ))}
          </ul>
          <p className="px-4 py-2.5 text-[11px] text-white/35 leading-relaxed border-t border-white/[0.04]">
            Green = a successful run inside the expected window. A cron that is firing but failing (or being rejected with a 401) files a critical finding and pushes to your devices.
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <LinkCard
          to="/staff?member=penny"
          title="Penny's desk"
          desc="The payment crons Audrey oversees — charge runs, escalation ladder, reconcilers."
          accent
        />
        <LinkCard
          to="/past-due"
          title="Past Due"
          desc="Where imminent-default findings point — review a booking before the 15:00 UTC run flips it."
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Silas' desk — application-security findings + coverage board
// ---------------------------------------------------------------------------

// Same severity palette as Audrey's ledger.
const SILAS_SEVERITY_STYLES = AUDREY_SEVERITY_STYLES;

function SilasDesk({ buildAuthHeader }) {
  const { desk, loading, error, reload } = useDesk('silas', buildAuthHeader);

  if (error) return <DeskError error={error} onRetry={reload} />;
  const counts = desk?.counts;
  const findings = desk?.open_findings || [];
  const lastRun = desk?.last_run;
  const coverage = desk?.coverage;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <DeskHeading icon={ShieldCheck}>Application security sweep</DeskHeading>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center justify-center text-[12.5px] font-medium text-white/65 hover:text-white w-10 h-10 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          aria-label="Refresh security desk"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Verdict tiles: open criticals is Silas' alarm bell — a critical here
          is also emailed to the founders the moment it first appears. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile
          label="Open criticals"
          value={counts?.critical ?? '—'}
          tone={counts?.critical > 0 ? 'text-rose-300' : 'text-emerald-300'}
          loading={loading}
        />
        <StatTile
          label="Open warnings"
          value={counts?.warning ?? '—'}
          tone={counts?.warning > 0 ? 'text-amber-300' : 'text-white'}
          loading={loading}
        />
        <StatTile label="Info notes" value={counts?.info ?? '—'} loading={loading} />
        <StatTile
          label="Last sweep"
          value={lastRun?.started_at ? formatRelativeTime(lastRun.started_at) : '—'}
          tone={lastRun && lastRun.ok === false ? 'text-rose-300' : 'text-white'}
          loading={loading}
        />
      </div>

      {/* Open findings ledger — each carries remediation language. */}
      <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
        <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40 px-4 pt-3.5 pb-1">Open findings</p>
        {!loading && findings.length === 0 ? (
          <div className="px-4 pb-4 pt-1 flex items-center gap-2 text-[12.5px] text-white/60">
            <CheckCircle2 className="w-4 h-4 text-emerald-300" strokeWidth={1.75} />
            {lastRun
              ? 'Nothing open — the last sweep found no security or availability issues.'
              : 'No sweeps recorded yet — the 4x-daily cron starts filing after the next deploy.'}
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {findings.slice(0, 25).map((f) => {
              const style = SILAS_SEVERITY_STYLES[f.severity] || SILAS_SEVERITY_STYLES.info;
              return (
                <li key={f.id} className="px-4 py-2.5">
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-medium uppercase tracking-wide flex-shrink-0 ${style.chip}`}>
                      {style.label}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12.5px] text-white/80 leading-snug">{f.title}</p>
                      {f.recommendation ? (
                        <p className="text-[11.5px] text-white/50 mt-1 leading-snug">
                          <span className="text-white/35">Fix: </span>{f.recommendation}
                        </p>
                      ) : null}
                      <p className="text-[11px] text-white/35 mt-0.5">
                        {f.check_key} · first seen {formatRelativeTime(f.first_seen_at)}
                        {f.emailed_at ? ' · founders emailed' : ''}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {findings.length > 25 ? (
          <p className="px-4 pb-3 text-[11px] text-white/35">…and {findings.length - 25} more.</p>
        ) : null}
      </div>

      {/* Coverage board — what each sweep probes. */}
      {coverage ? (
        <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
          <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40 px-4 pt-3.5 pb-1">What each sweep checks</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/[0.04]">
            {[
              { label: 'Pages / health', value: coverage.pages },
              { label: 'Auth gates', value: coverage.auth_gates },
              { label: 'Sensitive tables (RLS)', value: coverage.sensitive_tables },
              { label: 'Env vars', value: coverage.env_vars },
              { label: 'Security headers', value: coverage.headers },
              { label: 'TLS + assets', value: 'live' },
            ].map((c) => (
              <div key={c.label} className="bg-[#0b0e14] px-4 py-2.5">
                <p className="text-[15px] font-semibold text-white/90 tabular-nums">{c.value}</p>
                <p className="text-[11px] text-white/40 mt-0.5">{c.label}</p>
              </div>
            ))}
          </div>
          <p className="px-4 py-2.5 text-[11px] text-white/35 leading-relaxed border-t border-white/[0.04]">
            Probes run from outside the app over HTTPS, four times a day. A new critical (site down, broken deploy, an exposed route or table) is emailed to the founders immediately — not held for the twice-daily report. Silas reports; he never changes anything.
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <LinkCard
          to="/staff?member=audrey"
          title="Audrey's desk"
          desc="Operations audit — the payment crons, webhooks, and default ladder Audrey oversees."
          accent
        />
        <LinkCard
          to="/staff"
          title="Manager briefings"
          desc="Silas' security summary joins the twice-daily founder report on the Staff home."
        />
      </div>
    </div>
  );
}

/** Fallback desk so a new backend-registered teammate never breaks the page. */
function GenericDesk({ member }) {
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-6 text-center">
      <p className="text-[13px] text-white/70 font-normal">{member.name} is on the team.</p>
      <p className="text-[12px] text-white/40 mt-1 max-w-sm mx-auto leading-relaxed">
        No dedicated workspace has been wired up for this role yet — headline numbers appear on the roster card above.
      </p>
    </div>
  );
}

// Member id → desk component. Adding a teammate with a custom workspace
// is one entry here; everyone else gets GenericDesk automatically.
const DESK_REGISTRY = {
  stephanie: StephanieDesk,
  tommy: TommyDesk,
  vera: VeraDesk,
  marcus: MarcusDesk,
  penny: PennyDesk,
  audrey: AudreyDesk,
  silas: SilasDesk,
};

// ---------------------------------------------------------------------------
// Manager briefings — founder-facing reports with dismiss / reply threads
// ---------------------------------------------------------------------------

/** Markdown-lite renderer for briefing bodies: blank-line paragraphs,
 *  consecutive "- " lines become a list. Content is plain text from our own
 *  managers (and rendered as React text nodes — no HTML injection risk). */
function BriefingBody({ text }) {
  const blocks = String(text || '').split(/\n{2,}/).filter((b) => b.trim() !== '');
  return (
    <div className="space-y-2">
      {blocks.map((block, bi) => {
        const lines = block.split('\n').filter((l) => l.trim() !== '');
        const items = [];
        let para = [];
        const flushPara = () => {
          if (para.length) {
            items.push(<p key={`p${items.length}`} className="text-[12.5px] text-white/75 leading-relaxed">{para.join(' ')}</p>);
            para = [];
          }
        };
        let bullets = [];
        const flushBullets = () => {
          if (bullets.length) {
            items.push(
              <ul key={`u${items.length}`} className="space-y-1 pl-4 list-disc marker:text-white/30">
                {bullets.map((b, i) => (
                  <li key={i} className="text-[12.5px] text-white/75 leading-relaxed">{b}</li>
                ))}
              </ul>,
            );
            bullets = [];
          }
        };
        for (const line of lines) {
          if (/^\s*-\s+/.test(line)) {
            flushPara();
            bullets.push(line.replace(/^\s*-\s+/, ''));
          } else {
            flushBullets();
            para.push(line.trim());
          }
        }
        flushPara();
        flushBullets();
        return <div key={bi} className="space-y-2">{items}</div>;
      })}
    </div>
  );
}

const SLOT_LABELS = { morning: 'Morning report', evening: 'Evening report', manual: 'On-demand report' };

function BriefingCard({ briefing, manager, buildAuthHeader, onDismissed, onActed }) {
  const [messages, setMessages] = useState(briefing.messages || []);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState(null);

  const post = useCallback(async (path, body) => {
    const authHeader = await buildAuthHeader();
    if (!authHeader) throw new Error('Session expired — please sign back in.');
    const res = await fetch(path, {
      method: 'POST',
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
    return data;
  }, [buildAuthHeader]);

  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const data = await post(`/api/admin/staff/briefings/${briefing.id}/respond`, { message: reply.trim() });
      setMessages((prev) => [...prev, data.adminMessage, data.managerMessage].filter(Boolean));
      setReply('');
      // The manager may have executed an amendment on this turn — the
      // amendments panel is showing stale rows until it reloads.
      if (data.managerMessage?.actions?.some((a) => a.ok)) onActed?.();
    } catch (err) {
      setError(err?.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    setError(null);
    try {
      await post(`/api/admin/staff/briefings/${briefing.id}/dismiss`);
      onDismissed(briefing.id);
    } catch (err) {
      setError(err?.message || 'Failed to dismiss.');
      setDismissing(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4">
      <div className="flex items-start gap-3">
        <Avatar member={manager} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[13px] font-medium text-white">{manager.name}</span>
            <span className="text-[11px] text-white/40">{manager.title}</span>
            <span className="text-[11px] text-white/30 tabular-nums">
              {SLOT_LABELS[briefing.slot] || 'Report'} · {formatRelativeTime(briefing.created_at)}
            </span>
          </div>
          <div className="mt-2">
            <BriefingBody text={briefing.summary} />
          </div>
        </div>
      </div>

      {/* Follow-up thread */}
      {messages.length ? (
        <div className="mt-3 space-y-2 border-t border-white/[0.05] pt-3">
          {messages.map((m) => (
            <div key={m.id} className={`flex gap-2.5 ${m.role === 'admin' ? '' : 'pl-4'}`}>
              {m.role === 'manager' ? (
                <CornerDownRight className="w-3.5 h-3.5 text-white/25 mt-1 flex-shrink-0" strokeWidth={2} />
              ) : null}
              <div className={`rounded-xl px-3 py-2 min-w-0 ${m.role === 'admin' ? 'bg-brand-500/[0.08] border border-brand-400/15' : 'bg-white/[0.03] border border-white/[0.06]'}`}>
                <p className="text-[10px] uppercase tracking-wider font-medium text-white/35 mb-1">
                  {m.role === 'admin' ? (m.author || 'Founder') : manager.name}
                </p>
                <BriefingBody text={m.body} />
                {/* Receipts: what the manager actually executed this turn,
                    recorded at execution time — not taken from her prose. */}
                {m.actions?.length ? (
                  <div className="mt-2 space-y-1">
                    {m.actions.map((a, i) => (
                      <p
                        key={i}
                        className={`text-[11px] flex items-start gap-1.5 ${a.ok ? 'text-emerald-300/80' : 'text-amber-300/80'}`}
                      >
                        {a.ok ? (
                          <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={2} />
                        ) : (
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={2} />
                        )}
                        <span>
                          {a.ok
                            ? `${a.type === 'revert_amendment' ? 'Reverted' : 'Issued'} amendment: ${a.title || a.amendment_id}`
                            : `${a.type === 'revert_amendment' ? 'Revert' : 'Amendment'} refused${a.reason ? ` — ${a.reason}` : ''}`}
                        </span>
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {error ? <p className="mt-2 text-[11.5px] text-rose-300">{error}</p> : null}

      {/* Actions */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {replyOpen ? (
          <div className="w-full space-y-2">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={2}
              maxLength={4000}
              placeholder={`Ask ${manager.name} a follow-up…`}
              className="w-full rounded-xl bg-white/[0.03] border border-white/[0.1] px-3 py-2 text-base md:text-[12.5px] text-white placeholder-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 resize-y"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={sendReply}
                disabled={sending || !reply.trim()}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-white bg-brand-500/80 hover:bg-brand-500 disabled:opacity-40 px-3.5 py-1.5 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
              >
                {sending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} /> : <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.75} />}
                {sending ? `${manager.name} is writing…` : 'Send'}
              </button>
              <button
                type="button"
                onClick={() => { setReplyOpen(false); setReply(''); }}
                className="text-[12px] text-white/50 hover:text-white px-2 py-1.5 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setReplyOpen(true)}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-white/80 hover:text-white bg-white/[0.05] hover:bg-white/[0.09] border border-white/[0.09] px-3.5 py-1.5 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
            >
              <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.75} />
              Reply
            </button>
            <button
              type="button"
              onClick={dismiss}
              disabled={dismissing}
              className="inline-flex items-center gap-1.5 text-[12px] font-normal text-white/50 hover:text-white/80 px-3 py-1.5 rounded-full transition disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
            >
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
              {dismissing ? 'Dismissing…' : 'Dismiss'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Open manager briefings: the twice-daily (8AM/6PM PT) founder reports from
 * Stephanie and Audrey, with dismiss + reply (consultant thread). Also
 * offers on-demand generation so a founder never has to wait for the cron.
 */
function ManagerBriefings({ roster, buildAuthHeader, onActed }) {
  const [briefings, setBriefings] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/staff/briefings', { headers: { Authorization: authHeader } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setBriefings(data.briefings || []);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load briefings.');
      setBriefings([]);
    }
  }, [buildAuthHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const generateNow = async () => {
    if (generating) return;
    setGenerating(true);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/staff/briefings/generate', {
        method: 'POST',
        headers: { Authorization: authHeader },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to generate briefings.');
    } finally {
      setGenerating(false);
    }
  };

  const managerFor = (id) => roster?.find((m) => m.id === id) || { id, name: id, title: '', avatar: null };

  return (
    <section className="mb-4 sm:mb-5" aria-label="Manager briefings">
      <div className="flex items-center justify-between gap-2 mb-2 px-0.5">
        <div className="flex items-baseline gap-2 min-w-0">
          <h2 className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-medium flex-shrink-0">
            Manager briefings
          </h2>
          <span className="hidden sm:inline text-[11px] text-white/30 truncate">
            — twice-daily reports (8AM &amp; 6PM PT, also emailed to support@)
          </span>
        </div>
        <button
          type="button"
          onClick={generateNow}
          disabled={generating}
          className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-white/65 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] px-3 py-1.5 rounded-full transition disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
        >
          <Sparkles className={`w-3.5 h-3.5 ${generating ? 'animate-pulse' : ''}`} strokeWidth={1.75} />
          {generating ? 'Writing…' : 'Generate now'}
        </button>
      </div>

      {error ? (
        <div className="mb-2 rounded-xl bg-amber-500/[0.06] border border-amber-400/20 px-3.5 py-2.5 text-[12px] text-amber-200/90">
          {error}
        </div>
      ) : null}

      {briefings === null ? (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] h-24 animate-pulse" aria-hidden="true" />
      ) : briefings.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] px-4 py-3.5 text-[12.5px] text-white/50">
          No open briefings. The managers report at 8AM and 6PM PT — or tap <span className="text-white/75">Generate now</span> for a fresh read.
        </div>
      ) : (
        <div className="space-y-3">
          {briefings.map((b) => (
            <BriefingCard
              key={b.id}
              briefing={b}
              manager={managerFor(b.manager_id)}
              buildAuthHeader={buildAuthHeader}
              onDismissed={(id) => setBriefings((prev) => (prev || []).filter((x) => x.id !== id))}
              onActed={onActed}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Manager amendments — the managers' standing changes, with one-click revert
// ---------------------------------------------------------------------------

const AMENDMENT_TARGET_LABELS = {
  'tommy-knowledge': "Tommy's knowledge base",
  'ops-playbook': 'Ops playbook',
};

function ManagerAmendments({ roster, buildAuthHeader, refreshKey = 0 }) {
  const [amendments, setAmendments] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/staff/amendments', { headers: { Authorization: authHeader } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setAmendments(data.amendments || []);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load amendments.');
      setAmendments([]);
    }
  }, [buildAuthHeader]);

  // Reloads on mount and whenever a manager acts in a briefing thread.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const revert = async (id) => {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/staff/amendments/${id}/revert`, {
        method: 'POST',
        headers: { Authorization: authHeader },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setAmendments((prev) => (prev || []).map((a) =>
        a.id === id ? { ...a, status: 'reverted', reverted_at: new Date().toISOString() } : a));
    } catch (err) {
      setError(err?.message || 'Failed to revert.');
    } finally {
      setBusyId(null);
    }
  };

  const active = (amendments || []).filter((a) => a.status === 'active');
  const reverted = (amendments || []).filter((a) => a.status === 'reverted').slice(0, 5);
  const managerName = (id) => roster?.find((m) => m.id === id)?.name || id;

  // Nothing to show and nothing ever issued — stay out of the way entirely.
  if (amendments !== null && amendments.length === 0 && !error) return null;

  return (
    <div className="-mt-1 mb-4 sm:mb-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
      >
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40">Manager amendments</span>
          <span className="text-[11px] text-white/30 tabular-nums">{active.length} active</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2} />
      </button>

      {open ? (
        <div className="px-4 pb-4 space-y-2.5">
          <p className="text-[11px] text-white/35 leading-relaxed">
            Standing changes the managers issued under their own authority — announced in their briefings for your notice. Reverting takes effect within a minute.
          </p>
          {error ? <p className="text-[11.5px] text-rose-300">{error}</p> : null}
          {active.map((a) => (
            <div key={a.id} className="rounded-xl bg-white/[0.02] border border-white/[0.06] px-3.5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[12.5px] text-white font-medium leading-snug">{a.title}</p>
                  <p className="text-[11px] text-white/40 mt-0.5">
                    {managerName(a.manager_id)} → {AMENDMENT_TARGET_LABELS[a.target] || a.target} · {formatRelativeTime(a.created_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => revert(a.id)}
                  disabled={busyId === a.id}
                  className="flex-shrink-0 text-[11.5px] font-medium text-rose-300/80 hover:text-rose-200 bg-rose-500/[0.06] hover:bg-rose-500/[0.12] border border-rose-400/20 px-2.5 py-1 rounded-full transition disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60"
                >
                  {busyId === a.id ? 'Reverting…' : 'Revert'}
                </button>
              </div>
              <p className="text-[12px] text-white/70 leading-relaxed mt-2">{a.content}</p>
              {a.rationale ? (
                <p className="text-[11px] text-white/40 leading-relaxed mt-1.5">Why: {a.rationale}</p>
              ) : null}
            </div>
          ))}
          {reverted.length ? (
            <div className="pt-1">
              <p className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/25 mb-1.5">Recently reverted</p>
              {reverted.map((a) => (
                <p key={a.id} className="text-[11.5px] text-white/30 line-through leading-relaxed">
                  {a.title} <span className="no-underline">· {managerName(a.manager_id)}</span>
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stephanie's lessons — her recurring coaching, made standing (migration 165).
// Each lesson rides in every desk reply; the counts say whether it's sticking.
// ---------------------------------------------------------------------------

export function StephanieLessons({ buildAuthHeader }) {
  const [lessons, setLessons] = useState(null);
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/staff/lessons', { headers: { Authorization: authHeader } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setLessons(data.lessons || []);
      setReport(data.reports?.[0] || null);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load lessons.');
      setLessons([]);
    }
  }, [buildAuthHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const switchOff = async (id) => {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/staff/lessons/${id}/revert`, { method: 'POST', headers: { Authorization: authHeader } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setLessons((prev) => (prev || []).map((l) => (l.id === id ? { ...l, status: 'reverted' } : l)));
    } catch (err) {
      setError(err?.message || 'Failed to switch the lesson off.');
    } finally {
      setBusyId(null);
    }
  };

  const active = (lessons || []).filter((l) => l.status === 'active');
  const counts = Object.fromEntries((report?.report?.lessons || []).map((l) => [l.code, l]));
  const stop = report?.report?.totals;

  // No lessons yet and no report — the weekly run hasn't happened.
  if (lessons !== null && lessons.length === 0 && !report && !error) return null;

  return (
    <div className="-mt-1 mb-4 sm:mb-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
      >
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-[0.14em] font-medium text-white/40">Stephanie&apos;s lessons</span>
          <span className="text-[11px] text-white/30 tabular-nums">{active.length} active</span>
          {stop ? (
            <span className="text-[11px] text-white/30 tabular-nums">· she&apos;d stop {stop.wouldBlockPct}% of replies</span>
          ) : null}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2} />
      </button>

      {open ? (
        <div className="px-4 pb-4 space-y-2.5">
          <p className="text-[11px] text-white/35 leading-relaxed">
            Her coaching kept repeating, so each Monday she turns what recurs into a standing lesson the desk reads before every reply: L lessons are how to talk, K lessons are facts the desk kept getting wrong or leaving out, each quoting the knowledge base it comes from. The numbers show how many replies broke a lesson this week and last; a lesson that works drops toward zero. Switching one off takes effect within a minute.
          </p>
          {report?.summary ? (
            <p className="text-[12px] text-white/70 leading-relaxed">{report.summary}</p>
          ) : null}
          {error ? <p className="text-[11.5px] text-rose-300">{error}</p> : null}
          {active.map((l) => {
            const c = counts[l.code];
            return (
              <div key={l.id} className="rounded-xl bg-white/[0.02] border border-white/[0.06] px-3.5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[12.5px] text-white font-medium leading-snug">
                      <span className="font-mono text-[11px] text-sky-300/80 mr-1.5">{l.code}</span>{l.title}
                      {/^K\d+$/i.test(l.code || '') ? (
                        <span className="ml-1.5 align-middle text-[9.5px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded text-amber-200/80 bg-amber-400/10">Knowledge</span>
                      ) : null}
                    </p>
                    <p className="text-[11px] text-white/40 mt-0.5 tabular-nums">
                      {c ? `Broken ${c.this_week}× this week · ${c.last_week}× last week` : 'New — first counts next Monday'} · {formatRelativeTime(l.created_at)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => switchOff(l.id)}
                    disabled={busyId === l.id}
                    className="flex-shrink-0 text-[11.5px] font-medium text-rose-300/80 hover:text-rose-200 bg-rose-500/[0.06] hover:bg-rose-500/[0.12] border border-rose-400/20 px-2.5 py-1 rounded-full transition disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60"
                  >
                    {busyId === l.id ? 'Switching off…' : 'Switch off'}
                  </button>
                </div>
                <p className="text-[12px] text-white/70 leading-relaxed mt-2">{l.lesson}</p>
                {l.rationale ? (
                  <p className="text-[11px] text-white/40 leading-relaxed mt-1.5">Why: {l.rationale}</p>
                ) : null}
                {l.evidence?.kb_quote ? (
                  <p className="text-[11px] text-white/40 leading-relaxed mt-1.5 italic">From the knowledge base: {l.evidence.kb_quote}</p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff DM — founder ↔ member chat. Portaled to <body> (same lesson as the
// fullscreen chart: the page tree contains transformed ancestors that eat
// fixed-position overlays). Mobile: full-screen sheet; desktop: right panel.
// ---------------------------------------------------------------------------

function StaffChatPanel({ member, buildAuthHeader, onClose }) {
  const [messages, setMessages] = useState(null); // null = loading history
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const authHeader = await buildAuthHeader();
        const res = await fetch(`/api/admin/staff/${member.id}/chat`, {
          headers: authHeader ? { Authorization: authHeader } : {},
        });
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        setMessages(res.ok && data.ok ? data.messages || [] : []);
        scrollToEnd();
        inputRef.current?.focus();
      } catch {
        if (alive) setMessages([]);
      }
    })();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      alive = false;
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setError(null);
    setSending(true);
    setDraft('');
    setMessages((m) => [...(m || []), { id: `local-${Date.now()}`, role: 'founder', content: text, created_at: new Date().toISOString() }]);
    scrollToEnd();
    try {
      const authHeader = await buildAuthHeader();
      const res = await fetch(`/api/admin/staff/${member.id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
        body: JSON.stringify({ message: text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'No reply.');
      setMessages((m) => [...(m || []), { id: `reply-${Date.now()}`, role: 'staff', content: data.reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      setError(e.message || `${member.name} couldn't answer just now.`);
    } finally {
      setSending(false);
      scrollToEnd();
      inputRef.current?.focus();
    }
  }, [draft, sending, member.id, member.name, buildAuthHeader, scrollToEnd]);

  return createPortal(
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true" aria-label={`Chat with ${member.name}`}>
      {/* Scrim — click to close (desktop nicety; the sheet covers mobile). */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 sm:inset-auto sm:right-4 sm:top-4 sm:bottom-4 sm:w-[420px] bg-[#0b0b0d] sm:rounded-3xl border border-white/[0.08] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-[max(env(safe-area-inset-top),12px)] sm:pt-3.5 pb-3 border-b border-white/[0.06] flex-shrink-0">
          <Avatar member={member} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold truncate">{member.name}</span>
              <ActiveDot />
            </div>
            <p className="text-[11px] text-white/45 truncate">{String(member.title || '').replace(/^AI\s+/, '')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/[0.06] border border-white/[0.1] text-white/60 hover:text-white hover:bg-white/[0.12] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
          {messages === null ? (
            <p className="text-[12px] text-white/40 text-center pt-8">Opening the conversation…</p>
          ) : messages.length === 0 ? (
            <div className="text-center pt-8 px-4">
              <p className="text-[13px] text-white/70">This is the start of your conversation with {member.name}.</p>
              <p className="text-[11.5px] text-white/35 mt-1.5">Ask about their findings, their numbers, or how the work is going — they answer from their live data.</p>
            </div>
          ) : (
            messages.map((m) => (
              m.role === 'staff' ? (
                <div key={m.id} className="flex items-end gap-2 max-w-[85%]">
                  <Avatar member={member} size="xs" />
                  <div className="rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/[0.05] px-3.5 py-2.5 text-[13px] leading-relaxed text-white/85 whitespace-pre-wrap">{m.content}</div>
                </div>
              ) : (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand-500/[0.28] border border-brand-400/[0.18] px-3.5 py-2.5 text-[13px] leading-relaxed text-white/95 whitespace-pre-wrap">{m.content}</div>
                </div>
              )
            ))
          )}
          {sending ? (
            <div className="flex items-end gap-2">
              <Avatar member={member} size="xs" />
              <div className="rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/[0.05] px-3.5 py-2.5">
                <span className="inline-flex gap-1 items-center" aria-label={`${member.name} is typing`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse [animation-delay:150ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse [animation-delay:300ms]" />
                </span>
              </div>
            </div>
          ) : null}
          {error ? <p className="text-[11.5px] text-red-300/80 text-center">{error}</p> : null}
        </div>

        {/* Composer */}
        <div className="flex items-end gap-2 px-3 pt-2 pb-[max(env(safe-area-inset-bottom),10px)] sm:pb-3 border-t border-white/[0.06] flex-shrink-0">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder={`Message ${member.name}…`}
            aria-label={`Message ${member.name}`}
            className="flex-1 resize-none rounded-2xl bg-white/[0.05] border border-white/[0.09] px-3.5 py-2.5 text-base md:text-[13px] leading-relaxed text-white placeholder:text-white/30 focus:outline-none focus:border-brand-400/50 max-h-32"
          />
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim() || sending}
            aria-label="Send message"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-brand-500/80 text-white disabled:opacity-30 hover:bg-brand-500 transition flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          >
            <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// All Team group chat — one thread, whole management bench, router-answered
// ---------------------------------------------------------------------------

function AllTeamChatPanel({ staff, buildAuthHeader, onClose }) {
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  // Meeting mode: the founder convenes several heads to weigh in together,
  // each building on the ones before, instead of one router-picked answer.
  const [meeting, setMeeting] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const memberById = useCallback(
    (id) => (staff || []).find((m) => m.id === id) || { id, name: id, title: '', avatar: null },
    [staff],
  );

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const authHeader = await buildAuthHeader();
        const res = await fetch('/api/admin/all-team/chat', {
          headers: authHeader ? { Authorization: authHeader } : {},
        });
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        setMessages(res.ok && data.ok ? data.messages || [] : []);
        scrollToEnd();
        inputRef.current?.focus();
      } catch {
        if (alive) setMessages([]);
      }
    })();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      alive = false;
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setError(null);
    setSending(true);
    setDraft('');
    setMessages((m) => [...(m || []), { id: `local-${Date.now()}`, role: 'founder', content: text, created_at: new Date().toISOString() }]);
    scrollToEnd();
    try {
      const authHeader = await buildAuthHeader();
      const res = await fetch('/api/admin/all-team/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
        body: JSON.stringify({ message: text, mode: meeting ? 'meeting' : undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'No reply.');
      if (data.meeting && Array.isArray(data.contributions)) {
        setMessages((m) => [
          ...(m || []),
          ...data.contributions.map((c, i) => ({
            id: `meet-${Date.now()}-${i}`, role: 'staff', responder_id: c.responder?.id || null,
            content: c.reply, created_at: new Date().toISOString(),
          })),
        ]);
      } else {
        setMessages((m) => [...(m || []), {
          id: `reply-${Date.now()}`, role: 'staff', responder_id: data.responder?.id || null,
          content: data.reply, created_at: new Date().toISOString(),
        }]);
      }
    } catch (e) {
      setError(e.message || "The team couldn't answer just now.");
    } finally {
      setSending(false);
      scrollToEnd();
      inputRef.current?.focus();
    }
  }, [draft, sending, meeting, buildAuthHeader, scrollToEnd]);

  return createPortal(
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true" aria-label="Chat with the whole team">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 sm:inset-auto sm:right-4 sm:top-4 sm:bottom-4 sm:w-[440px] bg-[#0b0b0d] sm:rounded-3xl border border-white/[0.08] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-[max(env(safe-area-inset-top),12px)] sm:pt-3.5 pb-3 border-b border-white/[0.06] flex-shrink-0">
          <div className="h-9 w-9 rounded-full bg-brand-500/[0.18] border border-brand-400/25 flex items-center justify-center flex-shrink-0">
            <MessagesSquare className="w-4 h-4 text-brand-200" strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold truncate">All Team</span>
              <ActiveDot />
            </div>
            <p className="text-[11px] text-white/45 truncate">
              {meeting ? 'Meeting — the relevant heads weigh in together' : 'Ask anything — whoever has the answer replies'}
            </p>
          </div>
          {/* Meeting toggle: convene several heads to build on each other. */}
          <button
            type="button"
            onClick={() => setMeeting((v) => !v)}
            aria-pressed={meeting}
            title={meeting ? 'Meeting mode on — the bench deliberates together' : 'Convene a meeting — several heads weigh in'}
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 h-8 rounded-full border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 ${
              meeting
                ? 'bg-brand-500/[0.22] text-brand-100 border-brand-400/35'
                : 'bg-white/[0.05] text-white/55 border-white/[0.1] hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
            Meeting
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/[0.06] border border-white/[0.1] text-white/60 hover:text-white hover:bg-white/[0.12] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
          {messages === null ? (
            <p className="text-[12px] text-white/40 text-center pt-8">Opening the room…</p>
          ) : messages.length === 0 ? (
            <div className="text-center pt-8 px-4">
              <p className="text-[13px] text-white/60">You're talking to the whole management bench.</p>
              <p className="text-[11.5px] text-white/35 mt-1.5">Ask about revenue, risk, growth, fulfillment, anything — the department head best suited to answer picks it up, grounded in their live data.</p>
            </div>
          ) : (
            messages.map((m) => {
              if (m.role !== 'staff') {
                return (
                  <div key={m.id} className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand-500/[0.28] border border-brand-400/[0.18] px-3.5 py-2.5 text-[13px] leading-relaxed text-white/95 whitespace-pre-wrap">{m.content}</div>
                  </div>
                );
              }
              const responder = memberById(m.responder_id);
              return (
                <div key={m.id} className="max-w-[88%]">
                  <div className="flex items-center gap-1.5 mb-1 pl-1">
                    <span className="text-[11px] font-medium text-brand-200/90">{responder.name}</span>
                    <span className="text-[10px] text-white/35 truncate">{String(responder.title || '').replace(/^AI\s+/, '')}</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <Avatar member={responder} size="xs" />
                    <div className="rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/[0.05] px-3.5 py-2.5 text-[13px] leading-relaxed text-white/85 whitespace-pre-wrap">{m.content}</div>
                  </div>
                </div>
              );
            })
          )}
          {sending ? (
            <div className="flex items-center gap-2 pl-1">
              <span className="inline-flex gap-1 items-center" aria-label="The team is responding">
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse [animation-delay:300ms]" />
              </span>
              <span className="text-[11px] text-white/40">{meeting ? 'the room is deliberating…' : 'finding the right person…'}</span>
            </div>
          ) : null}
          {error ? <p className="text-[11.5px] text-red-300/80 text-center">{error}</p> : null}
        </div>

        {/* Composer */}
        <div className="flex items-end gap-2 px-3 pt-2 pb-[max(env(safe-area-inset-bottom),10px)] sm:pb-3 border-t border-white/[0.06] flex-shrink-0">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder={meeting ? 'Put a question to the room…' : 'Ask the team anything…'}
            aria-label="Message the whole team"
            className="flex-1 resize-none rounded-2xl bg-white/[0.05] border border-white/[0.09] px-3.5 py-2.5 text-base md:text-[13px] leading-relaxed text-white placeholder:text-white/30 focus:outline-none focus:border-brand-400/50 max-h-32"
          />
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim() || sending}
            aria-label="Send message"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-brand-500/80 text-white disabled:opacity-30 hover:bg-brand-500 transition flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
          >
            <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Workspace shell (profile header + desk)
// ---------------------------------------------------------------------------

function MemberWorkspace({ member, staff, departmentName, buildAuthHeader, onSelectMember, onDataChanged }) {
  const Desk = DESK_REGISTRY[member.id] || GenericDesk;
  const manager = member.reportsTo ? staff.find((s) => s.id === member.reportsTo) : null;
  const reports = staff.filter((s) => s.reportsTo === member.id);
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <section className="rounded-3xl border border-white/[0.07] bg-white/[0.015] p-4 sm:p-5 scroll-mt-24">
      {/* Profile header */}
      <div className="flex items-start gap-3.5 mb-4">
        <Avatar member={member} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-[17px] sm:text-[19px] font-semibold tracking-tight leading-tight">{member.name}</h2>
            {member.rank === 'manager' ? <ManagerBadge /> : null}
            <ActiveDot />
          </div>
          <p className="text-[12.5px] text-brand-300/90 font-medium mt-0.5">{member.title}</p>
        </div>
        <button
          type="button"
          onClick={() => setChatOpen(true)}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 h-9 rounded-full bg-brand-500/[0.16] border border-brand-400/[0.25] text-brand-200 hover:bg-brand-500/[0.28] hover:text-white transition flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
        >
          <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.75} />
          Message
        </button>
      </div>
      {chatOpen ? (
        <StaffChatPanel member={member} buildAuthHeader={buildAuthHeader} onClose={() => setChatOpen(false)} />
      ) : null}

      <p className="text-[13px] text-white/60 leading-relaxed mb-3">{member.summary}</p>

      <div className="flex items-center gap-1.5 flex-wrap mb-5">
        {departmentName ? <MetaChip icon={Users}>{departmentName}</MetaChip> : null}
        <MetaChip icon={Cpu}>{member.model}</MetaChip>
        <MetaChip icon={Radio}>{member.surface}</MetaChip>
        {manager ? (
          <button
            type="button"
            onClick={() => onSelectMember(manager.id)}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 rounded-full"
          >
            <MetaChip icon={CornerDownRight}>Reports to {manager.name}</MetaChip>
          </button>
        ) : null}
        {reports.length > 0 ? (
          <button
            type="button"
            onClick={() => onSelectMember(reports[0].id)}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 rounded-full"
          >
            <MetaChip icon={Crown}>Oversees {reports.map((r) => r.name).join(', ')}</MetaChip>
          </button>
        ) : null}
      </div>

      <Desk member={member} staff={staff} buildAuthHeader={buildAuthHeader} onSelectMember={onSelectMember} onDataChanged={onDataChanged} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Staff() {
  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [staff, setStaff] = useState(null); // null = loading; array = loaded
  const [departments, setDepartments] = useState(FALLBACK_DEPARTMENTS);
  const [rosterError, setRosterError] = useState(null);
  // Bumped when a manager executes an amendment inside a briefing thread, so
  // the amendments panel below reloads instead of showing stale rows.
  const [amendmentsVersion, setAmendmentsVersion] = useState(0);

  const initialParams = new URLSearchParams(location.search);
  const initialMember = initialParams.get('member') || 'stephanie';
  const [selectedId, setSelectedId] = useState(initialMember);
  // 'web' (radial org web, default) | 'grid' (department card sections).
  const [view, setView] = useState(initialParams.get('view') === 'grid' ? 'grid' : 'web');
  // Full-hierarchy toggle: weave the human leadership tier (About Us) into
  // the org web above the AI departments. Display-only.
  const [fullTree, setFullTree] = useState(false);
  // All Team group chat — address the whole management bench; the router
  // hands each question to the head best suited to answer.
  const [allTeamOpen, setAllTeamOpen] = useState(false);
  const workspaceRef = useRef(null);

  const switchView = useCallback((nextView) => {
    setView(nextView);
    const next = new URLSearchParams(location.search);
    if (nextView === 'grid') next.set('view', 'grid');
    else next.delete('view');
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await UserEntity.me();
        if (!cancelled) setUser(me || null);
      } catch {
        /* signed out */
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const buildAuthHeader = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const tok = data?.session?.access_token;
      return tok ? `Bearer ${tok}` : null;
    } catch {
      return null;
    }
  }, []);

  const loadRoster = useCallback(async () => {
    setRosterError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) {
        setStaff(FALLBACK_STAFF);
        return;
      }
      const res = await fetch('/api/admin/staff', { headers: { Authorization: authHeader } });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok && Array.isArray(data.staff) && data.staff.length) {
        setStaff(data.staff);
        if (Array.isArray(data.departments) && data.departments.length) {
          setDepartments(data.departments);
        }
      } else {
        setStaff(FALLBACK_STAFF);
        if (res.status !== 401 && res.status !== 403) {
          setRosterError('Live team stats are unavailable right now — showing the static roster.');
        }
      }
    } catch {
      setStaff(FALLBACK_STAFF);
      setRosterError('Live team stats are unavailable right now — showing the static roster.');
    }
  }, [buildAuthHeader]);

  const admin = isCurrentUserAdmin(user);

  useEffect(() => {
    if (!authReady || !admin) return;
    loadRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, admin]);

  // Selection: URL-mirrored (?member=) so desks are deep-linkable.
  // id === null deselects entirely (root-node click): the workspace below
  // hides until a teammate is picked again. Ephemeral by design — a reload
  // falls back to the default member.
  const selectMember = useCallback((id, { scroll = true } = {}) => {
    setSelectedId(id);
    const next = new URLSearchParams(location.search);
    if (id === 'stephanie' || id == null) next.delete('member');
    else next.set('member', id);
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    }
    if (scroll && id != null) {
      requestAnimationFrame(() => {
        workspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const roster = useMemo(() => staff || [], [staff]);
  const selectedMember = useMemo(() => {
    // null = explicitly deselected via the Defera root node; the unknown-id
    // fallback below is only for stale/bad ?member= URL params.
    if (!roster.length || selectedId === null) return null;
    return roster.find((m) => m.id === selectedId) || roster.find((m) => m.id === 'stephanie') || roster[0];
  }, [roster, selectedId]);

  // Org structure: department sections (rank-ordered) + reporting labels.
  const sections = useMemo(() => groupByDepartment(roster, departments), [roster, departments]);

  // Org-web inputs. Full-hierarchy mode prepends the leadership tier (a
  // noLane section — no department lane) and rewires each AI department's
  // top members to the executive who oversees it (EXEC_OVERSIGHT), with
  // lanes reordered so every executive's departments sit contiguously.
  const { treeSections, treeRoster } = useMemo(() => {
    if (!fullTree) return { treeSections: sections, treeRoster: roster };
    const orderOf = (id) => {
      const i = FULL_TREE_DEPT_ORDER.indexOf(id);
      return i === -1 ? FULL_TREE_DEPT_ORDER.length : i;
    };
    const ordered = [...sections].sort((a, b) => orderOf(a.id) - orderOf(b.id));
    const rewired = roster.map((m) => (
      m.reportsTo ? m : { ...m, reportsTo: EXEC_OVERSIGHT[m.department] || 'jayjay' }
    ));
    return {
      treeSections: [
        { id: 'leadership', name: 'Leadership', noLane: true, members: LEADERSHIP },
        ...ordered,
      ],
      treeRoster: [...LEADERSHIP, ...rewired],
    };
  }, [fullTree, sections, roster]);
  const relationshipById = useMemo(() => {
    const nameById = Object.fromEntries(roster.map((m) => [m.id, m.name]));
    const overseesById = {};
    for (const m of roster) {
      if (m.reportsTo) (overseesById[m.reportsTo] ||= []).push(m.name);
    }
    const labels = {};
    for (const m of roster) {
      if (m.reportsTo) labels[m.id] = `Reports to ${nameById[m.reportsTo] || m.reportsTo}`;
      else if (overseesById[m.id]?.length) labels[m.id] = `Oversees ${overseesById[m.id].join(', ')}`;
    }
    return labels;
  }, [roster]);
  const selectedDepartmentName = useMemo(() => {
    if (!selectedMember) return null;
    return sections.find((s) => s.id === (selectedMember.department || 'team'))?.name || null;
  }, [sections, selectedMember]);

  // --- Gates ---------------------------------------------------------------

  if (authReady && !user) {
    return (
      <div className="min-h-[60vh] bg-black text-white">
        <div className="max-w-3xl mx-auto px-4 py-10 text-center">
          <h1 className="text-2xl font-semibold mb-3">Staff</h1>
          <p className="text-sm text-white/60 mb-4">Sign in to continue.</p>
          <Button asChild size="sm" className="bg-white text-black hover:bg-white/90">
            <Link to="/home?auth_mode=login">Sign in</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Strict admin-only gate — the server already 403s every data route; this
  // renders a clean restricted state if a customer lands here from a bookmark.
  if (authReady && user && !admin) {
    return (
      <div className="min-h-[60vh] bg-black text-white">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <h1 className="text-2xl font-semibold mb-4">Staff</h1>
          <div className="rounded-2xl border border-red-400/30 bg-white/[0.02] p-4 flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white mb-0.5">This page is restricted</p>
              <p className="text-white/60 text-xs">
                Staff is an internal admin tool. If you think you should have access, contact support.
              </p>
              <div className="mt-3">
                <Button asChild size="sm" variant="outline" className="h-7 text-xs text-white border-white/20 bg-transparent hover:bg-white/10">
                  <Link to="/dashboard">Back to Trips</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Staff — Defera Admin</title>
      </Helmet>

      <div className="min-h-[100dvh] bg-black text-white">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 pt-4 sm:pt-6 pb-10 sm:pb-14">
          {/* Header — stacks on mobile so the controls never crowd the
              title; side-by-side from sm up. */}
          <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-5 sm:mb-6">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 font-normal mb-1.5 flex items-center gap-1.5">
                <Users className="w-3 h-3" strokeWidth={2} />
                AI Team
              </p>
              <h1 className="text-[22px] sm:text-[26px] font-semibold tracking-tight leading-none">Staff</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end">
              {/* Inquiries desk — the dashboard for working the concierge
                  queue (list + chat context), promoted out of Stephanie's
                  DailyReport panel below. */}
              <Link
                to="/staff/inquiries"
                className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full border border-amber-400/25 bg-amber-500/[0.10] text-amber-200 hover:bg-amber-500/[0.18] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
              >
                <Inbox className="w-3.5 h-3.5" strokeWidth={1.75} />
                Inquiries
              </Link>
              {/* All Team: one chat, whole management bench — the head with
                  the answer replies. */}
              <button
                type="button"
                onClick={() => setAllTeamOpen(true)}
                className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full border border-brand-400/25 bg-brand-500/[0.14] text-brand-200 hover:bg-brand-500/[0.22] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
              >
                <MessagesSquare className="w-3.5 h-3.5" strokeWidth={1.75} />
                All Team
              </button>
              {staff ? (
                <p className="hidden sm:block text-[11.5px] text-white/40 pb-0.5">
                  {roster.length} teammate{roster.length === 1 ? '' : 's'} · {sections.length} department{sections.length === 1 ? '' : 's'}
                </p>
              ) : null}
              {/* Full-hierarchy toggle: weave the About Us leadership tier
                  into the org web. Only meaningful on the web view. */}
              {view === 'web' ? (
                <button
                  type="button"
                  onClick={() => setFullTree((v) => !v)}
                  aria-pressed={fullTree}
                  className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 ${
                    fullTree
                      ? 'bg-white/[0.09] text-white border-white/[0.16]'
                      : 'bg-white/[0.04] text-white/50 border-white/[0.08] hover:text-white'
                  }`}
                >
                  <Crown className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Full hierarchy
                </button>
              ) : null}
              {/* View toggle: org web (default) vs department grid */}
              <div className="flex items-center rounded-full bg-white/[0.04] border border-white/[0.08] p-0.5" role="group" aria-label="Roster layout">
                {[
                  { id: 'web', label: 'Web', icon: Share2 },
                  { id: 'grid', label: 'Grid', icon: LayoutGrid },
                ].map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => switchView(v.id)}
                    aria-pressed={view === v.id}
                    className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 ${
                      view === v.id ? 'bg-white/[0.09] text-white' : 'text-white/50 hover:text-white'
                    }`}
                  >
                    <v.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          </header>

          {rosterError ? (
            <div className="mb-3 rounded-xl bg-amber-500/[0.06] border border-amber-400/20 px-3.5 py-2.5 text-[12px] text-amber-200/90">
              {rosterError}
            </div>
          ) : null}

          {/* Org roster. Default: the radial org web (Defera at the core,
              rank = ring, department = sector, strands = reporting lines).
              Toggle: department card sections, managers first — better for
              scanning stats on a dense team. Both scale with the roster. */}
          {staff === null ? (
            view === 'web' ? (
              <div className="rounded-3xl border border-white/[0.06] bg-white/[0.015] mb-4 sm:mb-5 h-[380px] sm:h-[440px] animate-pulse" aria-hidden="true" />
            ) : (
              <div className={ROSTER_CONTAINER_CLASS}>
                <RosterSkeletonCard />
                <RosterSkeletonCard />
              </div>
            )
          ) : view === 'web' ? (
            <OrgWeb
              sections={treeSections}
              roster={treeRoster}
              selectedId={selectedMember?.id || null}
              onSelect={(id) => selectMember(id)}
              showTitles={fullTree}
              storyLeadership={LEADERSHIP}
              storyOversight={EXEC_OVERSIGHT}
              storyDeptOrder={FULL_TREE_DEPT_ORDER}
            />
          ) : (
            sections.map((dept) => (
              <section key={dept.id} className="mb-4 sm:mb-5" aria-label={`${dept.name} department`}>
                <div className="flex items-baseline gap-2 mb-2 px-0.5 min-w-0">
                  <h2 className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-medium flex-shrink-0">
                    {dept.name}
                  </h2>
                  <span className="text-[10.5px] text-white/30 tabular-nums flex-shrink-0">{dept.members.length}</span>
                  {dept.blurb ? (
                    <span className="hidden sm:inline text-[11px] text-white/30 truncate">— {dept.blurb}</span>
                  ) : null}
                </div>
                <div className={ROSTER_CONTAINER_CLASS} role="tablist" aria-label={`${dept.name} members`}>
                  {dept.members.map((member) => (
                    <RosterCard
                      key={member.id}
                      member={member}
                      selected={selectedMember?.id === member.id}
                      onSelect={(id) => selectMember(id)}
                      relationshipLabel={relationshipById[member.id] || null}
                    />
                  ))}
                </div>
              </section>
            ))
          )}

          {/* Manager briefings — founder reports with dismiss/reply. Rendered
              once the roster is in so cards can show manager identity. */}
          {staff !== null ? (
            <>
              <ManagerBriefings
                roster={roster}
                buildAuthHeader={buildAuthHeader}
                onActed={() => setAmendmentsVersion((v) => v + 1)}
              />
              <ManagerAmendments
                roster={roster}
                buildAuthHeader={buildAuthHeader}
                refreshKey={amendmentsVersion}
              />
              <StephanieLessons buildAuthHeader={buildAuthHeader} />
            </>
          ) : null}

          {/* Selected member's workspace */}
          <div ref={workspaceRef}>
            {selectedMember ? (
              <MemberWorkspace
                key={selectedMember.id}
                member={selectedMember}
                staff={roster}
                departmentName={selectedDepartmentName}
                buildAuthHeader={buildAuthHeader}
                onSelectMember={(id) => selectMember(id)}
                onDataChanged={loadRoster}
              />
            ) : null}
          </div>
        </div>
      </div>
      {allTeamOpen ? (
        <AllTeamChatPanel
          staff={roster}
          buildAuthHeader={buildAuthHeader}
          onClose={() => setAllTeamOpen(false)}
        />
      ) : null}
    </>
  );
}
